import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LiveAvatarSession, SessionEvent, SessionState, AgentEventsEnum } from '@heygen/liveavatar-web-sdk';
import { Send, X, ChevronUp, ChevronDown, Mic, MicOff, Info } from 'lucide-react';
import { DISCLAIMER_TEXT } from './LegalDisclaimer';
import { isActiveVoiceState } from './voiceState';
import UserJourney, { STORAGE_KEY as USER_JOURNEY_STORAGE_KEY } from './UserJourney';
import FeedbackModal from './FeedbackModal';
import { findWakePhrase, extractAfterWake } from './wakeWord';
import { pushDataLayerEvent, trackButtonClick } from '../utils/tracking';
import { mergeTranscriptFragment, sanitizeTranscriptForChat, looksLikeAvatarEcho } from '../utils/transcriptSanitizer';
import { useVisualViewport, scrollFieldAboveKeyboard } from '../hooks/useVisualViewport';
import './AvatarComponent.css';
import './LegalDisclaimer.css';

// App branding
const APP_NAME = 'Afterlife Bot';
const APP_SUBTITLE = 'Speak with President Lincoln';
const FOOTER_TEXT = 'An Educational AI Reenactment of President Lincoln. Responses are for historical learning purposes.';
const LOGO_URL = 'https://actiontourguide.com/wp-content/uploads/2023/03/image.png';
// Display name in chat header and for saved session analysis
const AVATAR_DISPLAY_NAME = 'The President Lincoln';
// Name stored on every saved session (avatar_name) and shown in the dashboard,
// transcripts and PDF exports. The persona name, not the app name.
const SESSION_SAVE_NAME = 'President Lincoln';

const DEFAULT_AVATAR_ID = '6079ce31-6da1-4c6b-9b12-22ebafa9677f';
const DEFAULT_VOICE_ID = '69eb96ac-08ad-4b73-b4a4-ee6266732722';
const AVATAR_OUTPUT_VOLUME = 0.9;
// Mic capture constraints — browser-level echo/noise handling so chaotic background
// chatter is suppressed and the engine focuses on clear, nearby speech.
const MIC_AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
// Ignore final transcripts below this confidence WHEN the engine reports one — filters
// distant/overlapping speakers in a crowd. 0 disables it. Browsers that report no
// confidence (often Android) are unaffected. Override via REACT_APP_MIN_SPEECH_CONFIDENCE.
const MIN_SPEECH_CONFIDENCE = Number(process.env.REACT_APP_MIN_SPEECH_CONFIDENCE || 0.4);
// Auto-mute the user's mic while the avatar is speaking so side conversations aren't
// picked up as interruptions. It's a soft mute — the user can tap Unmute to barge in.
const AUTO_MUTE_WHILE_SPEAKING = true;
// Kept to about 8 seconds. The old greeting ran 29 seconds with the visitor's mic muted
// throughout, and 63% of visitors who never spoke had left within 30 seconds - before
// Lincoln finished introducing himself. It also promised "you can interrupt anytime"
// while the mic was muted.
const OPENING_GREETING = "My fellow citizens, I greet you from across time. Ask me anything about my life, the Civil War, or the places you'll visit. Just speak, or tap a question below.";
// About 4 in 10 visitors who started a session never asked anything. Phones have no text
// box, so a visitor whose mic is blocked, unsupported (in-app browsers) or awkward to use in
// public had no way in. One tap on these sends the question exactly as if it were typed.
const STARTER_QUESTIONS = [
  'What was your childhood like?',
  'Why did the Civil War begin?',
  'Which tours do you recommend?',
];

// The server answers within its own 12s budget (CHAT_DEADLINE_MS) - with a fallback line if
// no model responds - so the browser waits a little longer and normally hears that reply.
const CHAT_CLIENT_TIMEOUT_MS = 18000;
// Spoken when the reply cannot be fetched at all. Mirrors FALLBACK_LINES in
// services/lincolnPersona.js, which covers the case where the server is reachable.
const LINCOLN_FALLBACK_LINES = [
  'Forgive me, my friend, my thoughts wandered for a moment. Would you ask me that once more?',
  'Pardon me, I lost the thread there. Could you put that question to me again?',
  'My apologies, the telegraph is slow today. Would you mind asking me that again?',
];
const pickLincolnFallback = () => LINCOLN_FALLBACK_LINES[Math.floor(Math.random() * LINCOLN_FALLBACK_LINES.length)];

/** End session automatically after this much silence (no chat / speech / in-flight reply). */
const SESSION_IDLE_MS = 3 * 60 * 1000;
const SESSION_IDLE_CHECK_MS = 10 * 1000;

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => { if (isDev) console.log(...args); };
const warn = (...args) => { if (isDev) console.warn(...args); };

// President Lincoln's varied wake responses — cycles through each session so it never feels robotic.
const WAKE_GREETINGS = [
  'Yes? How may I help you?',
  'You have my attention. Please speak.',
  'What weighs upon your mind?',
  'I am listening. What would you know?',
  'Speak freely. What is your question?',
  'Yes, I hear you. How may I assist?',
  'Come now, what is it you wish to know?',
  'Proceed. I am all ears.',
  'You have found me. What can I do for you?',
  'I am here. What troubles you today?',
];

// Normalize chat text to sentence case for cleaner message display.
const toSentenceCase = (text) => {
  const input = String(text || '').trim();
  if (!input) return '';
  const lower = input.toLowerCase();
  return lower.replace(/(^\s*[a-z])|([.!?]\s+[a-z])|(\n\s*[a-z])/g, (match) =>
    match.toUpperCase()
  );
};

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));

function AvatarComponent() {
  // Configuration – from server /api/config (AVATAR_ID, VOICE_ID in .env) or frontend env or defaults
  const [avatarID, setAvatarID] = useState(process.env.REACT_APP_AVATAR_ID || DEFAULT_AVATAR_ID);
  const [voiceID, setVoiceID] = useState(process.env.REACT_APP_VOICE_ID || DEFAULT_VOICE_ID);
  // Use same-origin proxy to avoid CORS: browser calls our backend, backend calls LiveAvatar
  // Call LiveAvatar API directly (no proxy). If you see CORS errors, restore the proxy in server.js and api/liveavatar/
  const API_URL = 'https://api.liveavatar.com';
  
  // State
  const [chatInput, setChatInput] = useState('');
  const [sessionActive, setSessionActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isActivelyListening, setIsActivelyListening] = useState(false); // Track if we're actively listening (mic button clicked)
  const [isUserSpeaking, setIsUserSpeaking] = useState(false); // Derived from SR events (best-effort)
  const [mode, setMode] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  // Mobile: default full avatar (chat panel hidden); desktop: chat panel visible
  const [chatPanelVisible, setChatPanelVisible] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? false : true
  );
  const [mobileChatExpanded, setMobileChatExpanded] = useState(false); // false = split view (video top, chat card bottom), true = full-screen chat
  const [visualViewportStyle, setVisualViewportStyle] = useState(null); // mobile: { top, left, width, height } so layout stays fixed when keyboard opens
  const [showLegalBeforeStart, setShowLegalBeforeStart] = useState(false); // Show legal modal every time before starting session
  const [legalConsentChecked, setLegalConsentChecked] = useState(false); // Require consent before starting session
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [videoReady, setVideoReady] = useState(false); // True once remote video track is attached and playing
  const [isThinking, setIsThinking] = useState(false); // True while waiting for AI response
  const [sessionEndNotice, setSessionEndNotice] = useState('');
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSessionId, setFeedbackSessionId] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState(null);
  const headerMenuRef = useRef(null);
  const [isMicMuted, setIsMicMuted] = useState(false); // User-controlled mic mute
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false); // True when browser blocks microphone
  const [hasMicDevice, setHasMicDevice] = useState(true); // False when no microphone input device is present
  const [voicePermissionHelpVisible, setVoicePermissionHelpVisible] = useState(false); // iOS: show when stuck in Active without receiving speech
  const [voicePermissionHelpType, setVoicePermissionHelpType] = useState('both'); // 'microphone' | 'speech-recognition' | 'both'
  const [showUserJourney, setShowUserJourney] = useState(false);
  const [userJourneyMode, setUserJourneyMode] = useState('short'); // short = welcome-only; full = full 7-step
  /** Android SR debug: event log lines (dev or REACT_APP_ANDROID_SR_DEBUG=true). */
  const [androidSrDebugLines, setAndroidSrDebugLines] = useState([]);
  /** Android SR debug: ref snapshot refreshed ~1s (session, SR active, wake, etc.). */
  const [androidSrDebugSnapshot, setAndroidSrDebugSnapshot] = useState(null);

  // iOS: Safari blocks unmuted autoplay until user gesture; show "Tap to hear avatar" only on iOS
  const isIOS = typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const isMotoAndroid = Boolean(isAndroid && /moto|motorola/i.test(String(navigator?.userAgent || '')));
  // Audio-monitor gating reduces Android mic on/off sounds, but can prevent SR from starting
  // on some devices. Make it opt-in for production reliability.
  const ANDROID_USE_AUDIO_MONITOR = process.env.REACT_APP_ANDROID_USE_AUDIO_MONITOR === 'true';
  // Android only: optional second mic stream for OS beep suppression. Default OFF — two mics often break Web Speech.
  // Set REACT_APP_ANDROID_MIC_KEEPALIVE=true to enable (iOS unchanged; this flag is ignored on non-Android).
  const ANDROID_MIC_KEEPALIVE_ENABLED = isAndroid && process.env.REACT_APP_ANDROID_MIC_KEEPALIVE === 'true';
  // Bottom debug strip: development builds, or set REACT_APP_ANDROID_SR_DEBUG=true (e.g. Vercel) for device testing.
  const ANDROID_SR_DEBUG_UI = Boolean(
    isAndroid && (isDev || process.env.REACT_APP_ANDROID_SR_DEBUG === 'true')
  );

  useEffect(() => {
    if (showLegalBeforeStart) {
      setLegalConsentChecked(false);
      setEmailError('');
    }
  }, [showLegalBeforeStart]);

  useEffect(() => {
    if (!showLegalBeforeStart) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showLegalBeforeStart]);

  // Email + terms gate is the first thing shown when the link opens — the user must
  // go through it before they can use the bot. Prefill any previously stored email.
  useEffect(() => {
    let storedEmail = '';
    try {
      storedEmail = window.localStorage.getItem('afterlife-bot-user-email') || '';
    } catch (_) {}
    // Prefill the box for convenience, but do NOT populate userEmailRef here: the
    // address only counts once the user accepts the gate, so a returning visitor
    // cannot dismiss it and start a session on a remembered email.
    if (storedEmail && isValidEmail(storedEmail)) {
      setEmailInput(normalizeEmail(storedEmail));
    }
    pendingStartRef.current = false; // Opened on load, not via a Start click → don't auto-start
    setShowLegalBeforeStart(true);
  }, []);

  const androidSrDebugLog = useCallback((msg) => {
    if (!ANDROID_SR_DEBUG_UI) return;
    console.warn('[Android SR]', msg);
    const ts = new Date().toISOString().slice(11, 23);
    setAndroidSrDebugLines((prev) => [...prev.slice(-24), `${ts} ${msg}`]);
  }, [ANDROID_SR_DEBUG_UI]);

  // Older iOS Safari often delays or never emits "final" SpeechRecognition results.
  // Enable a conservative fallback that can accept longer interim phrases as queries.
  const iOSMajorVersion = (() => {
    if (typeof navigator === 'undefined') return null;
    const ua = String(navigator.userAgent || '');
    const m = ua.match(/OS (\d+)_\d+(_\d+)? like Mac OS X/i);
    if (!m) return null;
    const v = Number(m[1]);
    return Number.isFinite(v) ? v : null;
  })();
  const isLegacyIOSSafari = Boolean(isIOS && iOSMajorVersion != null && iOSMajorVersion <= 16);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Refs
  const avatarInstanceRef = useRef(null);
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatMessagesContainerRef = useRef(null);
  const sessionIdRef = useRef(null); // Store session_id for API calls
  const sessionTokenRef = useRef(null); // Store session token for Authorization header
  const isActivelyListeningRef = useRef(false); // Ref to track active listening state for event handlers
  const recognitionRef = useRef(null); // Ref for browser Speech Recognition API
  const sessionStartTimeRef = useRef(null); // When session became active (for saving history)
  const hasSavedThisSessionRef = useRef(false); // Prevent duplicate final save on End Chat / idle timeout
  const saveSessionAsEndedRef = useRef(null); // Final save when the avatar connection drops
  const isClosingRef = useRef(false); // Prevent duplicate close handler runs
  const chatHistorySaveChainRef = useRef(Promise.resolve()); // Serial realtime saves (DB + Sheets)
  const lastPersistedMessageCountRef = useRef(0);
  const isThinkingRef = useRef(false);
  const isStreamReadyRef = useRef(false); // Track stream readiness for greeting
  const isConnectedRef = useRef(false); // Track connected state for greeting
  const hasSpokenGreetingRef = useRef(false); // Ensure greeting is spoken once per session
  const messageIdRef = useRef(0); // Unique id for each message (avoids Date.now() key collisions)
  const prevSessionActiveRef = useRef(false); // Track session active transition so we only switch to chat when session ends
  const ttsAudioRef = useRef(null); // No longer used for ElevenLabs; kept for safe cleanup
  const sessionActiveRef = useRef(false);
  const isMicMutedRef = useRef(false);
  const recentAvatarSpeechRef = useRef([]); // Last few avatar lines — used to reject speaker echo
  const manualMuteRef = useRef(false); // True only when the USER muted via the button (survives auto-mute)
  const autoMutedForSpeechRef = useRef(false); // True while we auto-muted because the avatar is speaking
  // Silent mute gate: recognition keeps running while muted (so the OS never plays
  // mic on/off sounds) and results are discarded here instead.
  const micGateRef = useRef(false);
  const isAvatarTalkingRef = useRef(false);
  const pendingGoodbyeDisconnectRef = useRef(false);
  const chatMessagesRef = useRef([]);
  const handleCloseRef = useRef(null);
  const userEmailRef = useRef('');
  const pendingStartRef = useRef(false); // True when the email/terms gate was opened by a Start click (auto-start on agree)
  const legalModalScrollRef = useRef(null);
  const emailInputRef = useRef(null);
  const { visibleViewport: legalViewport, keyboardOpen: legalKeyboardOpen, shellStyle: legalShellStyle } = useVisualViewport(showLegalBeforeStart);

  const openFeedbackModal = useCallback(() => {
    trackButtonClick('open_feedback_modal', { page: 'avatar' });
    setHeaderMenuOpen(false);
    setFeedbackSessionId(sessionIdRef.current);
    setFeedbackModalOpen(true);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(false);
  }, []);
  const lastSpeechResultTimeRef = useRef(null); // Detect when stuck in Active without receiving speech (iOS)
  const hasReceivedAnySpeechRef = useRef(false); // Track if we've ever gotten a recognition result this session
  const activeListeningStartTimeRef = useRef(null); // When we entered Active/Listening
  const micWarmupDoneRef = useRef(false); // Ensure we only prompt for mic permission once
  const voicePermissionHelpShownRef = useRef(false); // Prevent repeating popup + speech
  const lastMicGrantedAtRef = useRef(0); // Cooldown: don't assume SR is blocked right after user grants mic
  const micPermissionStateRef = useRef('unknown'); // 'unknown' | 'granted' | 'denied' – for correct permission popup
  const voiceAbortControllerRef = useRef(null); // Abort voice input when user taps to interrupt
  const chatAbortControllerRef = useRef(null); // Abort chat request when user taps to interrupt
  const glowCleanupRef = useRef(null); // Cleanup fn for WebAudio glow analyzer
  const wakeActiveRef = useRef(false); // True after "hey president lincoln" until we submit the question
  const wakeCoalesceRef = useRef({ timer: null, transcripts: [] });
  const recentSpeechRef = useRef(''); // Rolling buffer to catch wake phrase split across utterances
  const wasSessionActiveRef = useRef(false); // For end-of-session UI cleanup
  const wakeCountRef = useRef(0); // How many times wake fired this session (for greeting rotation)
  const submitWakeQueryRef = useRef(null); // Stable pointer to latest submit fn (used by SR/timer callbacks)
  const isResponseInFlightRef = useRef(false); // Prevent overlapping /api/chat requests causing duplicate answers
  const lastSubmittedUserTextRef = useRef(''); // Deduplicate rapid duplicate SR submits
  const lastSubmittedCompactTextRef = useRef(''); // Stronger dedupe against interim/final variants
  const lastSubmittedAtRef = useRef(0);
  const micKeepAliveRef = useRef(null); // Android: silent getUserMedia stream held open to suppress mic on/off sounds
  const audioMonitorRef = useRef(null); // Android: RAF id for audio-level SR gating (post-wake)
  const sessionWakeActivatedRef = useRef(false); // Android: true after first wake word — enables audio-gated SR
  /** Android: coalesce rapid onend (silence/no-speech) into one restart to cut OS mic sounds. */
  const androidSrOnEndDebounceTimerRef = useRef(null);
  const avatarAudioRecoveryTimeoutRef = useRef(null); // Debounced retry to recover occasional muffled playback
  const showListeningAfterGreetingRef = useRef(false); // One-time UX hint after opening greeting
  const userSpeakingTimeoutRef = useRef(null); // Debounce clear for isUserSpeaking
  const lastSessionActivityAtRef = useRef(Date.now()); // Last user/avatar interaction (for idle auto-end)

  const touchSessionActivity = useCallback(() => {
    lastSessionActivityAtRef.current = Date.now();
  }, []);

  // Gate SR behind mic-level monitor only before wake; after greeting / wake, always run SR or users stay silent forever.
  const androidShouldGateSrWithAudioMonitor = () =>
    isAndroid && ANDROID_USE_AUDIO_MONITOR && !isMotoAndroid && !wakeActiveRef.current;

  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);

  useEffect(() => {
    isThinkingRef.current = isThinking;
  }, [isThinking]);

  useEffect(() => {
    if (!headerMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setHeaderMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [headerMenuOpen]);

  useEffect(() => {
    if (sessionActive && isThinking) touchSessionActivity();
  }, [sessionActive, isThinking, touchSessionActivity]);

  // Android SR debug: live ref snapshot for on-screen strip + remote debugging.
  useEffect(() => {
    if (!ANDROID_SR_DEBUG_UI) return;
    const tick = () => {
      setAndroidSrDebugSnapshot({
        session: !!sessionActiveRef.current,
        muted: !!isMicMutedRef.current,
        wake: !!wakeActiveRef.current,
        srActive: !!isActivelyListeningRef.current,
        uiListen: isListening,
        userSpeak: isUserSpeaking,
        avatarTalk: !!isAvatarTalkingRef.current,
        hasRec: !!recognitionRef.current,
        audioMon: !!audioMonitorRef.current,
        micKA: !!micKeepAliveRef.current,
        thinking: isThinking,
      });
    };
    tick();
    const id = setInterval(tick, 900);
    return () => clearInterval(id);
  }, [ANDROID_SR_DEBUG_UI, isListening, isUserSpeaking, isThinking]);

  useEffect(() => {
    if (!ANDROID_SR_DEBUG_UI) return;
    if (!sessionActive) {
      setAndroidSrDebugLines([]);
      setAndroidSrDebugSnapshot(null);
    }
  }, [ANDROID_SR_DEBUG_UI, sessionActive]);

  useEffect(() => {
    return () => {
      if (userSpeakingTimeoutRef.current) {
        clearTimeout(userSpeakingTimeoutRef.current);
        userSpeakingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!navigator?.mediaDevices?.enumerateDevices) return undefined;

    const refreshMicAvailability = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasAudioInput = devices.some((d) => d.kind === 'audioinput');
        setHasMicDevice(hasAudioInput);
      } catch (_) {
        // Keep previous state if device enumeration is blocked/unavailable.
      }
    };

    refreshMicAvailability();
    const onDeviceChange = () => {
      refreshMicAvailability();
    };
    navigator.mediaDevices.addEventListener?.('devicechange', onDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, []);

  const initGlowAnalyzer = (videoElement) => {
    if (!videoElement) return () => {};
    if (typeof window === 'undefined') return () => {};
    if (!window.AudioContext && !window.webkitAudioContext) return () => {};

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;

    let source;
    // Prefer MediaStreamSource for WebRTC video (createMediaElementSource fails on srcObject streams).
    // createMediaStreamSource only taps the stream for analysis — the video element keeps playing normally.
    const stream = videoElement.srcObject;
    if (stream && typeof stream.getAudioTracks === 'function' && stream.getAudioTracks().length > 0) {
      try {
        source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);
        // Do NOT connect analyser → destination; video element already handles playback.
      } catch (e) {
        try { audioCtx.close(); } catch (_) {}
        return () => {};
      }
    } else {
      // Fallback for <video src="..."> (non-WebRTC).
      try {
        source = audioCtx.createMediaElementSource(videoElement);
        source.connect(analyser);
        analyser.connect(audioCtx.destination);
      } catch (e) {
        try { audioCtx.close(); } catch (_) {}
        return () => {};
      }
    }

    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }

    const data = new Uint8Array(analyser.frequencyBinCount);
    let rafId = null;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / (data.length || 1); // 0–255
      const normalized = Math.min(1, Math.max(0, avg / 70)); // speech energy threshold
      const intensity = 1.0 + normalized * 0.45; // 1.0–1.45
      document.body?.style?.setProperty('--glow-intensity', intensity.toFixed(3));
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      stopped = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      try { source.disconnect(); } catch (_) {}
      try { analyser.disconnect(); } catch (_) {}
      try { audioCtx.close(); } catch (_) {}
    };
  };

  // Audio analyser: drives --glow-intensity so the ring pulses with the avatar's voice.
  // NOTE: The ring is now rendered as a real DOM <div> in JSX (not body pseudo-elements)
  // so it works correctly on Android Chrome where body::before with position:fixed is broken.
  useEffect(() => {
    if (!isTalking) {
      document.body.style.setProperty('--glow-intensity', '1');
      glowCleanupRef.current?.();
      glowCleanupRef.current = null;
      return;
    }
    const el = videoRef.current;
    if (el) {
      glowCleanupRef.current?.();
      glowCleanupRef.current = initGlowAnalyzer(el);
    }
    return () => {};
  }, [isTalking]);

  // Clean up glow analyser when session ends.
  useEffect(() => {
    if (!sessionActive) {
      document.body.style.setProperty('--glow-intensity', '1');
      glowCleanupRef.current?.();
      glowCleanupRef.current = null;
    }
  }, [sessionActive]);

  // Ensure speech recognition is running whenever a session is active (same retry path for iOS and Android).
  useEffect(() => {
    if (!sessionActive) return;
    if (isMicMuted) return;
    // Small delay avoids racing the stream attach on some mobile browsers.
    // Also retry a few times since some browsers silently fail the first start().
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8;

    const tryStart = () => {
      if (cancelled) return;
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      attempts += 1;
      try { androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition(); } catch (_) {}
      // On Android the audio monitor is self-sustaining; only retry on other browsers.
      if (!isAndroid && attempts < MAX_ATTEMPTS) {
        setTimeout(tryStart, 450);
      }
    };

    const t = setTimeout(tryStart, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [sessionActive, isMicMuted]);

  // Show onboarding only once (first run). Users can reopen via the help button.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(USER_JOURNEY_STORAGE_KEY) === 'true';
      if (!seen) {
        setUserJourneyMode('short');
        setShowUserJourney(true);
      }
    } catch (_) {}
  }, []);

  // Close onboarding when a session starts/ends so it never lingers over the avatar.
  useEffect(() => {
    const was = wasSessionActiveRef.current;
    wasSessionActiveRef.current = sessionActive;
    if (sessionActive) {
      setShowUserJourney(false);
      return;
    }
    if (was && !sessionActive) {
      setShowUserJourney(false);
    }
  }, [sessionActive]);

  useEffect(() => {
    if (!voicePermissionHelpVisible) {
      voicePermissionHelpShownRef.current = false;
    }
  }, [voicePermissionHelpVisible]);

  // iOS/Android: Detect when stuck in "Active" without receiving any speech – show permission help
  useEffect(() => {
    if (!sessionActive || isMicMuted || (!isIOS && !isAndroid)) return;
    const interval = setInterval(() => {
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      if (isAvatarTalkingRef.current) return; // Only show after avatar has finished speaking
      if (hasReceivedAnySpeechRef.current) return; // User has spoken successfully
      // Do not show the popup while recognition is running (background wake-word or active listening).
      if (isActivelyListeningRef.current) return;
      if (isListening) return;
      const start = activeListeningStartTimeRef.current;
      if (!start) return;
      const elapsed = Date.now() - start;
      // If we've been truly stuck (recognition not running) for more than 8 seconds,
      // show the permission help popup.
      if (elapsed >= 8000) {
        showVoicePermissionHelp('both');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionActive, isMicMuted, isIOS, isAndroid, isListening]);

  // Android/iOS recovery: sometimes SpeechRecognition is "running" but produces no results
  // (mic enabled, but user speech not captured). This watchdog restarts listening safely.
  useEffect(() => {
    if (!sessionActive || isMicMuted) return;
    let lastKickAt = 0;
    const interval = setInterval(() => {
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      if (isAvatarTalkingRef.current) return;
      const start = activeListeningStartTimeRef.current;
      if (!start) return;
      const elapsed = Date.now() - start;
      // If SR/monitor has been active for a while but no speech results ever arrived, restart.
      if (!hasReceivedAnySpeechRef.current && elapsed >= 7000) {
        const now = Date.now();
        if (now - lastKickAt < 8000) return; // avoid flapping
        lastKickAt = now;
        try {
          // Soft stop: keep keep-alive stream if present (reduces Android OS sounds).
          stopSpeechRecognition(true);
        } catch (_) {}
        // Reset timer base so we don't immediately kick again.
        activeListeningStartTimeRef.current = Date.now();
        try {
          stopAudioMonitoring();
          startSpeechRecognition();
        } catch (_) {}
      }
    }, 1000);
    return () => clearInterval(interval);
    // Intentionally omit startSpeechRecognition/startAudioMonitoring from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, isMicMuted, isAndroid]);

  // When truly stuck (session on, recognition not running, not muted/talking/thinking) show popup
  useEffect(() => {
    const inActiveState = isActiveVoiceState({
      sessionActive,
      isMicMuted,
      isTalking,
      isThinking,
      isListening,
      isActivelyListening, // background wake-word mode counts as listening
    });
    if (!inActiveState) {
      setVoicePermissionHelpType('both');
      if (isMobile) {
        setVoicePermissionHelpVisible(false); // Mobile can auto-dismiss when voice state recovers
      }
      return;
    }
    // On Android, "not actively listening" is expected — the audio-level monitor is running
    // instead of SR during silence. showVoicePermissionHelp() also checks audioMonitorRef.current
    // but this avoids scheduling the timeout altogether.
    if (isAndroid && audioMonitorRef.current) return;
    const t = setTimeout(() => {
      showVoicePermissionHelp('both');
    }, 8000); // longer timeout – only fire if genuinely stuck
    return () => clearTimeout(t);
  }, [sessionActive, isMicMuted, isTalking, isThinking, isListening, isActivelyListening, isMobile]);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);


  // (Removed) Do not auto-show onboarding on every "Start session" screen.

  // Load avatar/voice config from server .env
  useEffect(() => {
    fetch('/api/config')
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) {
          console.error('[Avatar] /api/config failed:', r.status, r.statusText);
          return {};
        }
        if (text.trim().toLowerCase().startsWith('<!')) {
          console.warn('[Avatar] /api/config returned HTML – backend may not be running. Local: use "npm run dev".');
          return {};
        }
        try {
          return JSON.parse(text);
        } catch (_) {
          return {};
        }
      })
      .then((data) => {
        setAvatarID(data.avatarId || DEFAULT_AVATAR_ID);
        setVoiceID(data.voiceId || DEFAULT_VOICE_ID);
      })
      .catch((e) => console.error('[Avatar] /api/config fetch error:', e?.message, e));
  }, []);

  // Catch "Session not found" from LiveAvatar SDK (rejected promises or thrown errors)
  useEffect(() => {
    const handleSessionNotFoundState = () => {
      saveSessionAsEndedRef.current?.();
      setErrorMessage('Session expired or invalid. Please click Stop then Start again.');
      setIsConnected(false);
      isConnectedRef.current = false;
      setSessionActive(false);
    };
    const onSessionNotFound = () => handleSessionNotFoundState();
    const onRejection = (event) => {
      const msg = (event?.reason?.message || String(event?.reason || '')).toLowerCase();
      if (msg.includes('session not found')) {
        event.preventDefault();
        event.stopPropagation?.();
        handleSessionNotFoundState();
      }
    };
    const onError = (event) => {
      const msg = (event?.error?.message || event?.message || '').toLowerCase();
      if (msg.includes('session not found')) {
        handleSessionNotFoundState();
        if (event.preventDefault) event.preventDefault();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        return true;
      }
      return false;
    };
    window.addEventListener('session-not-found', onSessionNotFound);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError, true);
    return () => {
      window.removeEventListener('session-not-found', onSessionNotFound);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onError, true);
    };
  }, []);

  // Mobile only: pin layout to visual viewport so app doesn't move when keyboard opens
  useEffect(() => {
    const vv = typeof window !== 'undefined' && window.visualViewport;
    if (!vv) return;
    const update = () => {
      requestAnimationFrame(() => {
        if (window.innerWidth >= 768) {
          setVisualViewportStyle(null);
          return;
        }
        setVisualViewportStyle({
          position: 'fixed',
          left: 0,
          top: vv.offsetTop,
          width: '100%',
          height: vv.height,
          maxHeight: vv.height,
        });
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Mobile: prevent window scroll when keyboard opens (stops header from being pushed off-screen)
  useEffect(() => {
    if (!isMobile) return;
    const handleScroll = () => { window.scrollTo(0, 0); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Detect if user message is a goodbye/farewell (triggers auto-disconnect after response)
  // Require minimum length to avoid false positives when user starts speaking
  const isGoodbyeMessage = (text) => {
    if (!text || typeof text !== 'string') return false;
    const t = text.trim().toLowerCase();
    if (t.length < 2) return false; // Avoid matching "I", "So", etc.
    const farewells = ['bye', 'goodbye', 'good bye', 'see you', 'see ya', 'take care', "i'm done", "im done", "that's all", 'thats all', 'farewell', 'later', 'gotta go', 'have to go'];
    // Require message to be primarily a farewell (exact match or ends with farewell)
    return farewells.some(f => t === f || t === f + '.' || t.endsWith(' ' + f) || t.endsWith(' ' + f + '.'));
  };

  const normalizeUserText = (text) => String(text || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const compactUserText = (text) => normalizeUserText(text)
    // Remove wake-style prefixes so "hey president lincoln ..." and plain question dedupe together.
    .replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(president\s+lincoln|lincoln|president)\s*[,\-:]*\s*/i, '')
    // Remove punctuation noise and collapse spaces.
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const isNearDuplicateText = (incomingCompact, previousCompact) => {
    if (!incomingCompact || !previousCompact) return false;
    if (incomingCompact === previousCompact) return true;
    if (incomingCompact.length >= 12 && previousCompact.length >= 12) {
      if (incomingCompact.startsWith(previousCompact) || previousCompact.startsWith(incomingCompact)) return true;
      if (incomingCompact.includes(previousCompact) || previousCompact.includes(incomingCompact)) return true;
    }
    const incomingTokens = incomingCompact.split(' ').filter(Boolean);
    const previousTokens = previousCompact.split(' ').filter(Boolean);
    if (incomingTokens.length < 2 || previousTokens.length < 2) return false;
    const tokenSet = new Set(previousTokens);
    const overlap = incomingTokens.filter((t) => tokenSet.has(t)).length;
    const ratio = overlap / Math.max(incomingTokens.length, previousTokens.length);
    return ratio >= 0.85;
  };
  const logDuplicateSkip = (reason, normalizedText, elapsedMs) => {
    if (process.env.NODE_ENV === 'production') return;
    const preview = String(normalizedText || '').slice(0, 140);
    console.debug(`[Avatar dedupe] skipped submit (${reason}) elapsed=${elapsedMs}ms text="${preview}"`);
  };
  const shouldSkipDuplicateSubmit = (text) => {
    const normalized = normalizeUserText(text);
    const compact = compactUserText(text);
    if (!normalized) return true;
    const now = Date.now();
    const elapsed = now - lastSubmittedAtRef.current;
    // Guard against delayed duplicate finals/interims from SR that can arrive long
    // after the first submit while the assistant is already answering/speaking.
    const isExactRepeat =
      normalized === lastSubmittedUserTextRef.current && elapsed < 30000;
    const isRapidDuplicate =
      normalized === lastSubmittedUserTextRef.current && elapsed < 2200;
    // Stronger window for SR interim/final variants that arrive slightly later or with tiny text drift.
    const isNearDuplicate =
      elapsed < 8000 && isNearDuplicateText(compact, lastSubmittedCompactTextRef.current);
    if (isResponseInFlightRef.current) {
      logDuplicateSkip('response-in-flight', normalized, elapsed);
      return true;
    }
    if (isRapidDuplicate) {
      logDuplicateSkip('rapid-exact-duplicate', normalized, elapsed);
      return true;
    }
    if (isNearDuplicate) {
      logDuplicateSkip('near-duplicate', normalized, elapsed);
      return true;
    }
    if (isExactRepeat) {
      logDuplicateSkip('exact-repeat-window', normalized, elapsed);
      return true;
    }
    lastSubmittedUserTextRef.current = normalized;
    lastSubmittedCompactTextRef.current = compact || normalized;
    lastSubmittedAtRef.current = now;
    return false;
  };

  // Add chat message (unique id so React always renders user + assistant bubbles)
  const addChatMessage = (text, sender) => {
    if (!text || !text.trim()) return;
    messageIdRef.current += 1;
    const isUserMessage = sender === 'user';
    if (!isUserMessage) {
      // Remember what the avatar says so the mic can't feed his own voice back in
      // as a user question (speaker echo). Keep only the last few utterances.
      recentAvatarSpeechRef.current = [
        String(text),
        ...recentAvatarSpeechRef.current,
      ].slice(0, 3);
    }
    const message = {
      id: `msg-${messageIdRef.current}-${Date.now()}`,
      content: isUserMessage ? String(text).trim() : toSentenceCase(text),
      role: isUserMessage ? 'user' : 'assistant',
      timestamp: new Date().toISOString(),
    };
    setChatMessages(prev => [...prev, message]);
    touchSessionActivity();
  };

  // Speak opening greeting once we are BOTH connected + stream-ready
  const maybeSpeakOpeningGreeting = async () => {
    if (hasSpokenGreetingRef.current) return;
    if (!isConnectedRef.current || !isStreamReadyRef.current) return;
    if (!avatarInstanceRef.current) return;

    hasSpokenGreetingRef.current = true;
    showListeningAfterGreetingRef.current = true;
    addChatMessage(OPENING_GREETING, 'avatar');
    await makeAvatarSpeak(OPENING_GREETING);
  };

  // Get access token from backend
  // When ElevenLabs is configured, use CUSTOM mode so we can send our audio via repeatAudio (lip-sync from our TTS)
  const getAccessToken = async () => {
    const requestToken = async (avatar, voice, mode = 'FULL') => {
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          avatar_id: avatar,
          voice_id: voice,
          mode,
          language: 'en',
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();
      if (contentType.includes('text/html') || text.trim().toLowerCase().startsWith('<!')) {
        throw new Error('BACKEND_NOT_AVAILABLE');
      }
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      if (!data.token) {
        throw new Error('Token not received from server');
      }

      // Store session_id and token if available (needed for API calls like /api/task)
      if (data.session_id) {
        sessionIdRef.current = data.session_id;
        log('✅ Session ID stored:', data.session_id);
      }
      if (data.token) {
        sessionTokenRef.current = data.token;
        log('✅ Session token stored');
      }

      return { token: data.token, sessionMode: mode };
    };

    // Resolve env-first: fetch config so we always use AVATAR_ID / VOICE_ID from .env when available
    let envAvatarId = avatarID;
    let envVoiceId = voiceID;
    try {
      const configRes = await fetch('/api/config');
      const configText = await configRes.text();
      if (configRes.ok && !configText.trim().toLowerCase().startsWith('<!')) {
        try {
          const config = JSON.parse(configText);
          if (config.avatarId) envAvatarId = config.avatarId;
          if (config.voiceId) envVoiceId = config.voiceId;
          setAvatarID(envAvatarId);
          setVoiceID(envVoiceId);
        } catch (_) {}
      }
    } catch (e) {
      console.error('[Avatar] Config fetch error:', e?.message, e);
    }

    const sessionMode = 'FULL';
    console.warn('[Avatar] Requesting token:', { sessionMode });
    try {
      // First attempt: always use env (config) ids; only use default if env didn't provide them
      const preferredAvatar = envAvatarId || DEFAULT_AVATAR_ID;
      const preferredVoice = envVoiceId || DEFAULT_VOICE_ID;
      const result = await requestToken(preferredAvatar, preferredVoice, sessionMode);
      console.warn('[Avatar] Token received:', { sessionMode: result?.sessionMode });
      return result;
    } catch (primaryError) {
      console.error('[Avatar] Token fetch error, falling back to FULL:', primaryError?.message, primaryError);
      try {
        setAvatarID(DEFAULT_AVATAR_ID);
        setVoiceID(DEFAULT_VOICE_ID);
        return await requestToken(DEFAULT_AVATAR_ID, DEFAULT_VOICE_ID, 'FULL');
      } catch (fallbackError) {
        console.error('[Avatar] Token fetch failed (default avatar/voice):', fallbackError?.message, fallbackError);
        throw fallbackError;
      }
    }
  };

  // One‑time mic permission warmup (triggers browser mic prompt via getUserMedia)
  const ensureMicPermission = async () => {
    if (micWarmupDoneRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_AUDIO_CONSTRAINTS });
      stream.getTracks().forEach(t => t.stop());
      micWarmupDoneRef.current = true;
      lastMicGrantedAtRef.current = Date.now();
      micPermissionStateRef.current = 'granted';
      setIsMicPermissionDenied(false);
    } catch (err) {
      console.error('Mic permission warmup error:', err);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.message?.toLowerCase?.().includes('permission');
      micPermissionStateRef.current = isDenied ? 'denied' : micPermissionStateRef.current;
      if (isDenied) setIsMicPermissionDenied(true);
      const hasNoMic = err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError';
      if (hasNoMic) setHasMicDevice(false);
    }
  };

  // ── Android mic keep-alive — AudioContext approach ──────────────────────────
  // DIFFERENT APPROACH: Instead of just holding a raw getUserMedia stream, we
  // route the mic through an AudioContext (getUserMedia → createMediaStreamSource
  // → createScriptProcessor → destination).  Keeping an active AudioContext
  // ── Android mic keep-alive ───────────────────────────────────────────────────
  // The VAD library suppressed mic-on/off sounds by maintaining a COMPLETE audio
  // pipeline (mic → AudioWorklet → destination).  Android's audio subsystem sees
  // a live output session and does not fire new audio-focus open/close events when
  // SpeechRecognition starts or stops — so the OS mic sounds never play.
  //
  // Our previous approach stopped at the AnalyserNode (no destination connection).
  // Android did NOT consider that an active session, so it still played sounds on
  // each SR cycle.
  //
  // Fix: route the mic through analyser → silentGain (gain=0) → destination.
  // gain=0 means absolutely no audio reaches the speaker (no echo/feedback risk),
  // but the audio graph is "complete" and Android treats it as active media output.
  const startMicKeepAlive = async () => {
    if (!isAndroid || !ANDROID_MIC_KEEPALIVE_ENABLED) return;
    if (micKeepAliveRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_AUDIO_CONSTRAINTS, video: false });
      micWarmupDoneRef.current = true;
      lastMicGrantedAtRef.current = Date.now();
      micPermissionStateRef.current = 'granted';
      setIsMicPermissionDenied(false);

      // eslint-disable-next-line no-undef
      const AudioCtx = window.AudioContext || window['webkitAudioContext'];
      if (AudioCtx) {
        try {
          const ctx = new AudioCtx();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 32;
          // Silent gain: gain=0 → no audio to speaker; but the complete graph
          // (source→analyser→gain→destination) keeps Android's audio session active.
          const silentGain = ctx.createGain();
          silentGain.gain.value = 0;
          src.connect(analyser);
          analyser.connect(silentGain);
          silentGain.connect(ctx.destination);
          ctx.resume().catch(() => {});
          micKeepAliveRef.current = { stream, ctx, src, analyser, silentGain };
          log('Mic keep-alive started (full pipeline → destination), ctx state:', ctx.state);
        } catch (ctxErr) {
          warn('Mic keep-alive AudioContext failed, stream-only fallback:', ctxErr?.message);
          micKeepAliveRef.current = stream;
        }
      } else {
        micKeepAliveRef.current = stream;
      }
    } catch (err) {
      warn('Mic keep-alive failed:', err?.message);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      if (isDenied) {
        micPermissionStateRef.current = 'denied';
        setIsMicPermissionDenied(true);
      }
      const hasNoMic = err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError';
      if (hasNoMic) setHasMicDevice(false);
    }
  };

  const ensureAndroidKeepAliveHealthy = () => {
    if (!isAndroid) return;
    const ka = micKeepAliveRef.current;
    const ctx = ka?.ctx;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const stream = ka?.stream || (ka?.getTracks ? ka : null);
    if (stream?.getAudioTracks && stream.getAudioTracks().length === 0) {
      micKeepAliveRef.current = null;
    }
  };

  // Keep Android mic capture open continuously for the full session.
  // If the keep-alive stream/context drops, rebuild it in the background.
  useEffect(() => {
    if (!isAndroid || !sessionActive || !ANDROID_MIC_KEEPALIVE_ENABLED) return;
    let cancelled = false;
    const ensureContinuous = () => {
      if (cancelled) return;
      if (!sessionActiveRef.current) return;
      ensureAndroidKeepAliveHealthy();
      if (!micKeepAliveRef.current) {
        startMicKeepAlive().catch(() => {});
      }
    };
    // Prime immediately, then watchdog every few seconds.
    ensureContinuous();
    const interval = setInterval(ensureContinuous, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // startMicKeepAlive/ensureAndroidKeepAliveHealthy intentionally omitted (ref-based, stable enough)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAndroid, sessionActive, ANDROID_MIC_KEEPALIVE_ENABLED]);

  const stopMicKeepAlive = () => {
    stopAudioMonitoring(); // always cancel audio poll before releasing the analyser
    const ref = micKeepAliveRef.current;
    if (!ref) return;
    try {
      const stream = ref.stream || (ref.getTracks ? ref : null);
      if (stream?.getTracks) stream.getTracks().forEach(t => t.stop());
      if (ref.src)         try { ref.src.disconnect();         } catch (_) {}
      if (ref.analyser)    try { ref.analyser.disconnect();    } catch (_) {}
      if (ref.silentGain)  try { ref.silentGain.disconnect();  } catch (_) {}
      if (ref.ctx)         try { ref.ctx.close();              } catch (_) {}
    } catch (_) {}
    micKeepAliveRef.current = null;
    log('Mic keep-alive stopped');
  };

  // ── Audio-level monitor — gates SR restart on Android after first wake word ──
  // Problem: SpeechRecognition restarts every ~5 s on Android (no-speech timeout),
  // and each restart plays OS mic-enable / mic-disable sounds even with the keep-alive
  // stream.  After the user says the wake phrase once we no longer need SR to run
  // continuously — we can instead watch the AnalyserNode in the keep-alive pipeline
  // and only start a new SR session when the user actually starts speaking.
  // During silence SR is NOT running → no OS sounds.
  const stopAudioMonitoring = () => {
    if (audioMonitorRef.current) {
      cancelAnimationFrame(audioMonitorRef.current);
      audioMonitorRef.current = null;
    }
  };

  const startAudioMonitoring = () => {
    if (!isAndroid) return;
    if (!ANDROID_USE_AUDIO_MONITOR) return;
    if (audioMonitorRef.current) return; // already running
    ensureAndroidKeepAliveHealthy();
    // Moto devices sometimes fail to produce meaningful analyser values, causing the gate
    // to never start SpeechRecognition. Use direct SR for reliability on Moto.
    if (isMotoAndroid) {
      startSpeechRecognition();
      return;
    }

    let ka = micKeepAliveRef.current;

    // No keep-alive stream at all — fall through to direct SR so the app still works.
    if (!ka) {
      log('Audio monitor: no keep-alive, using SR immediately and recovering keep-alive in background');
      // Critical for reliability on some Android devices (e.g. Moto): start SR now.
      // Do not wait for keep-alive recovery promise before listening.
      startSpeechRecognition();
      if (ANDROID_MIC_KEEPALIVE_ENABLED) {
        startMicKeepAlive().then(() => {
          if (!sessionActiveRef.current || isMicMutedRef.current) return;
          // Keep-alive recovered; future SR restarts can use monitor path.
        }).catch(() => {});
      }
      return;
    }

    // Keep-alive is a raw MediaStream (AudioContext creation failed in startMicKeepAlive).
    // Try to build the AudioContext now — we may be inside a user-gesture context still.
    if (!ka.analyser) {
      try {
        // eslint-disable-next-line no-undef
        const AudioCtx = window.AudioContext || window['webkitAudioContext'];
        if (AudioCtx) {
          const stream = ka.stream || (ka.getTracks ? ka : null);
          if (stream) {
            const ctx = new AudioCtx();
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 32;
            const silentGain = ctx.createGain();
            silentGain.gain.value = 0;
            src.connect(analyser);
            analyser.connect(silentGain);
            silentGain.connect(ctx.destination);
            ctx.resume().catch(() => {});
            micKeepAliveRef.current = { stream, ctx, src, analyser, silentGain };
            ka = micKeepAliveRef.current;
            log('Audio monitor: built AudioContext from existing stream');
          }
        }
      } catch (err) {
        warn('Audio monitor: AudioContext rebuild failed:', err?.message);
      }
      // Still no analyser after rebuild attempt → fall back to direct SR
      if (!ka.analyser) {
        log('Audio monitor: no analyser available, falling back to SR');
        startSpeechRecognition();
        return;
      }
    }

    // Ensure AudioContext is running before we start polling
    if (ka.ctx && ka.ctx.state === 'suspended') {
      ka.ctx.resume().catch(() => {});
    }

    const analyser = ka.analyser;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    let activeFrms = 0;
    let zeroFrms   = 0;
    let pollFrames = 0;
    const THRESHOLD      = 18;  // 0–255; speech typically 30–100, silence < 10–15
    const NEEDED         = 3;   // ~50 ms at 60 fps
    const ZERO_RESUME_AT = 90;  // ~1.5 s of all-zero → try AudioContext resume
    const ZERO_BAIL_AT   = 300; // ~5 s of all-zero → context is stuck, fall back to SR

    const poll = () => {
      if (!sessionActiveRef.current || isMicMutedRef.current) {
        audioMonitorRef.current = null;
        return;
      }
      pollFrames += 1;
      // If the gate never opens (analyser stuck between 1–THRESHOLD), still allow SR after ~9s.
      if (pollFrames > 540) {
        audioMonitorRef.current = null;
        warn('Audio monitor: timeout, falling back to direct SpeechRecognition');
        startSpeechRecognition();
        return;
      }
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i];
      const avg = sum / buf.length;

      // While the avatar is speaking its voice reaches the mic and would falsely
      // trigger SR every ~5 s (the no-speech timeout cycle).  Reset the counters
      // so we only start SR when the USER is the one making noise.
      if (isAvatarTalkingRef.current) {
        activeFrms = 0;
        zeroFrms   = 0;
        audioMonitorRef.current = requestAnimationFrame(poll);
        return;
      }

      if (avg > THRESHOLD) {
        activeFrms++;
        zeroFrms = 0;
        if (activeFrms >= NEEDED) {
          activeFrms = 0;
          audioMonitorRef.current = null; // stop poll — SR will take over
          startSpeechRecognition();
          return;
        }
      } else {
        activeFrms = 0;
        if (avg === 0) {
          zeroFrms++;
          // AudioContext may have been suspended by the OS — nudge it to resume
          if (zeroFrms === ZERO_RESUME_AT) {
            const ctx = micKeepAliveRef.current?.ctx;
            if (ctx && ctx.state !== 'closed') ctx.resume().catch(() => {});
          }
          // After 5 s of all-zeros the AudioContext is genuinely stuck — fall back to SR
          if (zeroFrms >= ZERO_BAIL_AT) {
            audioMonitorRef.current = null;
            warn('Audio monitor: context stuck (all zeros), falling back to SR cycling');
            startSpeechRecognition();
            return;
          }
        } else {
          zeroFrms = 0;
        }
      }
      audioMonitorRef.current = requestAnimationFrame(poll);
    };
    audioMonitorRef.current = requestAnimationFrame(poll);
    log('Audio monitor started');
  };

  // Re-assert avatar playback state to recover occasional mobile audio ducking/muffling.
  // This does not alter interruption flow; it only reinforces output gain and playback.
  const reinforceAvatarAudioPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.muted = false;
      if (typeof video.volume === 'number') video.volume = AVATAR_OUTPUT_VOLUME;
      const playPromise = video.play?.();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } catch (_) {}
    if (avatarAudioRecoveryTimeoutRef.current) {
      clearTimeout(avatarAudioRecoveryTimeoutRef.current);
    }
    avatarAudioRecoveryTimeoutRef.current = setTimeout(() => {
      const retryVideo = videoRef.current;
      if (!retryVideo) return;
      try {
        retryVideo.muted = false;
        if (typeof retryVideo.volume === 'number') retryVideo.volume = AVATAR_OUTPUT_VOLUME;
        const retryPromise = retryVideo.play?.();
        if (retryPromise && typeof retryPromise.catch === 'function') {
          retryPromise.catch(() => {});
        }
      } catch (_) {}
    }, 140);
  };

  // Android: do not start SpeechRecognition until the microphone is actually granted and capture
  // is ready (keep-alive pipeline if enabled, else one-shot warmup like iOS).
  const isAndroidMicReadyForSr = () => {
    if (!isAndroid) return true;
    if (micPermissionStateRef.current !== 'granted') return false;
    if (ANDROID_MIC_KEEPALIVE_ENABLED) return !!micKeepAliveRef.current;
    return micWarmupDoneRef.current;
  };

  // Start session
  const handleStart = async () => {
    if (isConnecting || isConnected) return;
    if (!isValidEmail(userEmailRef.current)) {
      pendingStartRef.current = true; // Came from Start → auto-start once email/terms accepted
      setShowLegalBeforeStart(true);
      return;
    }
    trackButtonClick('start_session', { page: 'avatar' });
    pushDataLayerEvent('session_start_attempt', { page: 'avatar' });
    // Ensure mic starts unmuted for each new session
    isMicMutedRef.current = false;
    setIsMicMuted(false);
    manualMuteRef.current = false;
    autoMutedForSpeechRef.current = false;
    micGateRef.current = false;

    setIsConnecting(true);
    setIsEndingSession(false);
    setErrorType(null);
    setErrorMessage(null);

    try {
      // Use this user gesture to pre-unlock video audio where allowed (especially for iOS/WebView)
      if (videoRef.current) {
        try {
          videoRef.current.muted = false;
          if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {});
          }
        } catch (e) {
          // Safe to ignore here; stream may not be attached yet
        }
      }

      if (isAndroid) {
        // Default: one mic path (warmup only) — best for Web Speech. Opt-in keep-alive via env.
        if (ANDROID_MIC_KEEPALIVE_ENABLED) {
          await startMicKeepAlive();
        } else {
          await ensureMicPermission();
        }
      } else {
        await ensureMicPermission();
      }

      // Skip mic warm-up: requesting then immediately releasing causes enable/disable sounds.
      // Permission will be requested when stream is ready and we actually need the mic.

      // Reset per-session flags
      isStreamReadyRef.current = false;
      isConnectedRef.current = false;
      hasSpokenGreetingRef.current = false;
      showListeningAfterGreetingRef.current = false;
      wakeActiveRef.current = false;
      sessionWakeActivatedRef.current = false;
      recentSpeechRef.current = '';
      {
        const wc = wakeCoalesceRef.current;
        if (wc.timer) clearTimeout(wc.timer);
        wc.timer = null;
        wc.transcripts = [];
      }
      hasSavedThisSessionRef.current = false;
      lastPersistedMessageCountRef.current = 0;
      hasReceivedAnySpeechRef.current = false;
      activeListeningStartTimeRef.current = null;
      setVoicePermissionHelpType('both');
      setVoicePermissionHelpVisible(false);
      // Sync mic permission state from Permissions API when supported (Chrome, etc.) so popup shows correct message
      if (typeof navigator?.permissions?.query === 'function') {
        navigator.permissions.query({ name: 'microphone' }).then((status) => {
          if (status?.state === 'granted') {
            micPermissionStateRef.current = 'granted';
            setIsMicPermissionDenied(false);
          } else if (status?.state === 'denied') {
            micPermissionStateRef.current = 'denied';
            setIsMicPermissionDenied(true);
          }
        }).catch(() => {});
      }

      // Clean up existing session
      if (avatarInstanceRef.current) {
        try {
          await avatarInstanceRef.current.stop();
        } catch (e) {
          // Ignore cleanup errors
        }
      }

      // Get token
      const tokenResult = await getAccessToken();
      const token = typeof tokenResult === 'object' && tokenResult !== null && tokenResult.token
        ? tokenResult.token
        : tokenResult;
      // Create session
      // Start with voiceChat: false - microphone will only be enabled when user clicks mic button
      const session = new LiveAvatarSession(token, {
        voiceChat: false, // Disable microphone initially - only enable when user clicks mic button
        apiUrl: API_URL
      });
      
      // session_id was already stored in sessionIdRef by getAccessToken(); use it for this session
      if (sessionIdRef.current) {
        session._sessionId = sessionIdRef.current;
        log('✅ Session ID stored:', sessionIdRef.current);
      }
      log('Session created:', {
        hasMessage: typeof session.message === 'function',
        hasRepeat: typeof session.repeat === 'function',
        sessionState: session.state,
        sessionInfo: session._sessionInfo
      });

      // Session state handler
      session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
        pushDataLayerEvent('session_state_changed', { page: 'avatar', session_state: String(state || 'unknown') });
        if (state === SessionState.CONNECTED) {
          pushDataLayerEvent('session_connected', { page: 'avatar' });
          setIsConnected(true);
          isConnectedRef.current = true;
          setMode('live');
          // Greeting may fire here if stream is already ready
          maybeSpeakOpeningGreeting().catch((err) => {
            if (!handleSessionNotFound(err)) console.error('[Avatar] Greeting failed:', err?.message || err);
          });
        } else if (state === SessionState.DISCONNECTED) {
          saveSessionAsEndedRef.current?.();
          setSessionActive(false);
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsTalking(false);
        }
      });

      // Stream ready handler – attach video as soon as stream is ready; video must be visible and have size
      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        pushDataLayerEvent('session_stream_ready', { page: 'avatar' });
        setSessionActive(true);
        // Also update the ref immediately so auto speech recognition
        // doesn't early‑return on a stale sessionActiveRef value.
        sessionActiveRef.current = true;
        sessionStartTimeRef.current = new Date().toISOString();
        touchSessionActivity();
        hasSavedThisSessionRef.current = false;
        lastPersistedMessageCountRef.current = 0;
        setSessionEndNotice('');
        setFeedbackModalOpen(false);
        setFeedbackSessionId(null);
        isStreamReadyRef.current = true;
        setVideoReady(false); // will flip to true once attach/play succeeds
        log('Session stream ready - avatar can now speak');

        const doAttach = () => {
          if (videoRef.current) {
            try {
              session.attach(videoRef.current);
              // Stream is ready – ensure video is unmuted so user hears avatar
              videoRef.current.muted = false;
              if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
              if (videoRef.current.paused) {
                videoRef.current.play().then(() => {
                  setVideoReady(true);
                }).catch(() => {});
              } else {
                setVideoReady(true);
              }
              log('Avatar video attached');
            } catch (error) {
              console.error('Video attachment error:', error);
            }
          }
        };
        doAttach();
        setTimeout(doAttach, 150);
        // Greeting may fire here if we're already connected
        setTimeout(() => {
          maybeSpeakOpeningGreeting().catch((err) => {
            if (!handleSessionNotFound(err)) console.error('[Avatar] Greeting failed:', err?.message || err);
          });
        }, 250);

        // Speech recognition: start when stream is ready. Android waits until mic is actually
        // enabled (permission + keep-alive or warmup) so SR does not run before capture exists.
        try {
          const runSr = () => {
            if (isAndroid) {
              wakeActiveRef.current = true;
              sessionWakeActivatedRef.current = true;
              log('Android: speech recognition starting with stream (no wake word required)');
              androidSrDebugLog('STREAM_READY → startSR (mic ready, wake primed)');
            }
            (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
          };

          if (isAndroid) {
            if (isAndroidMicReadyForSr()) {
              runSr();
            } else {
              androidSrDebugLog('STREAM_READY: defer SR until mic enabled');
              let attempts = 0;
              const iv = setInterval(() => {
                attempts += 1;
                if (!sessionActiveRef.current) {
                  clearInterval(iv);
                  return;
                }
                if (isAndroidMicReadyForSr()) {
                  clearInterval(iv);
                  runSr();
                  return;
                }
                if (attempts >= 50) {
                  clearInterval(iv);
                  if (micPermissionStateRef.current === 'denied') {
                    androidSrDebugLog('SR not started: microphone permission denied');
                    showVoicePermissionHelp('microphone');
                  } else {
                    androidSrDebugLog('SR start after mic wait timeout (permission not denied)');
                    wakeActiveRef.current = true;
                    sessionWakeActivatedRef.current = true;
                    (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
                  }
                }
              }, 200);
            }
          } else {
            (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
          }
        } catch (e) {
          androidSrDebugLog(`STREAM_READY startSR failed: ${e?.message || e}`);
          console.error('Auto startSpeechRecognition failed:', e?.message || e);
        }
      });

      // Disconnect handler
      session.on(SessionEvent.SESSION_DISCONNECTED, () => {
        pushDataLayerEvent('session_disconnected', { page: 'avatar' });
        androidSrDebugLog('SESSION_DISCONNECTED');
        // Mark session inactive and release microphone access when session disconnects
        saveSessionAsEndedRef.current?.();
        sessionActiveRef.current = false;
        stopSpeechRecognition();
        setSessionActive(false);
        setIsTalking(false);
        setIsConnected(false);
      });

      // Avatar speaking handlers
      session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        pushDataLayerEvent('avatar_speak_started', { page: 'avatar' });
        setIsTalking(true);
        setIsThinking(false); // Keep Thinking label until avatar actually starts speaking
        isAvatarTalkingRef.current = true;
        log('Avatar started speaking');
        reinforceAvatarAudioPlayback();
        setIsListening(false);
        recentSpeechRef.current = ''; // prevent avatar speech echo from triggering wake word
        // Auto-mute the mic while the avatar speaks so side/background conversations
        // aren't captured. SILENT soft mute: recognition keeps running (no OS mic
        // on/off sounds) — results are simply discarded via micGateRef. The user can
        // tap Unmute to interrupt (barge in).
        if (AUTO_MUTE_WHILE_SPEAKING && !manualMuteRef.current && sessionActiveRef.current) {
          autoMutedForSpeechRef.current = true;
          micGateRef.current = true;
          setIsMicMuted(true);
          const coalesce = wakeCoalesceRef.current;
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = null;
          coalesce.transcripts = [];
        }
        // Android: keep SpeechRecognition running during playback so barge-in (interim/final) works.
        // Pausing SR here made interruption impossible; iOS non-legacy already keeps SR active.
        if (isAndroid) {
          androidSrDebugLog('AVATAR_SPEAK_STARTED — keep SR for barge-in');
        }
        if (isLegacyIOSSafari && recognitionRef.current) {
          // Older iOS may route output to earpiece while SR is active.
          // Pause SR during playback; resume after speech end.
          try { recognitionRef.current.stop(); } catch (_) {}
          isActivelyListeningRef.current = false;
          setIsActivelyListening(false);
          setIsListening(false);
        }
      });

      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        pushDataLayerEvent('avatar_speak_ended', { page: 'avatar' });
        setIsTalking(false);
        isAvatarTalkingRef.current = false;
        log('Avatar finished speaking');
        androidSrDebugLog('AVATAR_SPEAK_ENDED → restart SR soon');
        // Auto-unmute after the avatar finishes — only if WE auto-muted and the user
        // didn't manually mute. Recognition never stopped, so reopening the gate is
        // silent (no OS mic sounds).
        if (autoMutedForSpeechRef.current && !manualMuteRef.current) {
          autoMutedForSpeechRef.current = false;
          micGateRef.current = false;
          setIsMicMuted(false);
          // Grace window: give the user's reply a clean, fresh capture right after the
          // avatar finishes so a slightly clipped opening still forms a full phrase.
          // (Silent — recognition was never stopped/restarted.)
          lastSubmittedUserTextRef.current = '';
          lastSubmittedCompactTextRef.current = '';
          recentSpeechRef.current = '';
          const coalesce = wakeCoalesceRef.current;
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = null;
          coalesce.transcripts = [];
        }
        // Android: keep conversation mode on (same as iOS after greeting).
        if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
          wakeActiveRef.current = true;
          sessionWakeActivatedRef.current = true;
        }
        if (showListeningAfterGreetingRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
          setIsListening(true);
          // After the opening greeting, allow immediate questions without requiring a wake word.
          // This also keeps the Listening label visible on Android (Moto/Samsung), where SR
          // may start in a "quiet" mode when wakeActiveRef is false.
          wakeActiveRef.current = true;
          showListeningAfterGreetingRef.current = false;
        }
        reinforceAvatarAudioPlayback();
        if (pendingGoodbyeDisconnectRef.current) {
          pendingGoodbyeDisconnectRef.current = false;
          setTimeout(() => {
            handleCloseRef.current?.();
          }, 2000);
        }
        // If the avatar just finished a wake-word greeting ("Yes? How may I help you?"),
        // reset the coalesce timer so the user gets a FULL window starting NOW
        if (wakeActiveRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
          const coalesce = wakeCoalesceRef.current;
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = setTimeout(() => submitWakeQueryRef.current?.(), 7000);
        }
        // Restore Listening badge + restart capture
        if (sessionActiveRef.current && !isMicMutedRef.current) {
          setIsListening(Boolean(wakeActiveRef.current));
          if (isAndroid) {
            // If SR stayed up during playback (barge-in path), only refresh UI — do not stop/recycle recognition.
            setTimeout(() => {
              if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
              if (isActivelyListeningRef.current) {
                setIsListening(Boolean(wakeActiveRef.current));
                androidSrDebugLog('AVATAR_SPEAK_ENDED: SR still active — skip recycle');
                return;
              }
              isActivelyListeningRef.current = false;
              setIsActivelyListening(false);
              const go = () =>
                androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
              requestAnimationFrame(() => setTimeout(go, 90));
            }, 520);
          } else if (isActivelyListeningRef.current) {
            /* desktop: SR still running */
          } else if (isLegacyIOSSafari) {
            setTimeout(() => {
              if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
              startSpeechRecognition();
            }, 650);
          } else {
            startSpeechRecognition();
          }
        }
      });

      // Note: We're not using SDK's voiceChat feature (voiceChat: false)
      // Instead, we use browser's Speech Recognition API when mic button is clicked
      // So we don't need USER_TRANSCRIPTION, USER_SPEAK_STARTED, or USER_SPEAK_ENDED handlers

      // Store session reference
      avatarInstanceRef.current = session;
      
      // Start session
      await session.start();
      
      // Fallback video attachment
      setTimeout(() => {
        if (videoRef.current && session.state === SessionState.CONNECTED) {
          try {
            session.attach(videoRef.current);
          } catch (error) {
            // Will retry on stream ready
          }
        }
      }, 500);
      
    } catch (error) {
      pushDataLayerEvent('session_start_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Session connection failed:', error);
      if (handleSessionNotFound(error)) return;
      const msg = (error?.message || '').trim();
      const errorName = error.name || '';

      // API returned HTML (index.html) – backend not running or not deployed
      const isHtmlResponse = msg === 'BACKEND_NOT_AVAILABLE' ||
        (msg.includes('Unexpected token') && (msg.includes('<!doctype') || msg.includes('not valid JSON')));
      if (isHtmlResponse) {
        setErrorType('GENERAL');
        setErrorMessage(
          'Backend not available: /api requests are returning the app page instead of JSON. ' +
          'Local: in the project folder run "npm run dev" (starts both backend on :5000 and app on :3000). ' +
          'Do not run only "npm start" — the backend must be running. ' +
          'Production: ensure the api/ folder is deployed (e.g. Vercel serverless functions) and API URL is same origin.'
        );
        setSessionActive(false);
        setIsConnected(false);
        setIsConnecting(false);
        return;
      }

      // Check for permission errors first
      if (errorName === 'NotAllowedError' ||
          msg.includes('Permission denied') ||
          msg.includes('permission')) {
        micPermissionStateRef.current = 'denied';
        setIsMicPermissionDenied(true);
        setErrorType('AUTH');
        setErrorMessage('End session first. Then go to Settings → Privacy & Security and enable Speech Recognition and Microphone for your browser. Ensure the site is served over HTTPS. Start a new session after enabling.');
      } else if (msg.includes('UNAUTHORIZED_KEY_TYPE') || 
          msg.includes('401') || 
          msg.toLowerCase().includes('unauthorized') || 
          msg.includes('400112')) {
        setErrorType('AUTH');
        setErrorMessage("A 'Streaming' API Key is required for live video. Please check your API key configuration.");
      } else if (msg.toLowerCase().includes('avatar') && msg.toLowerCase().includes('not found')) {
        setErrorType('GENERAL');
        setErrorMessage('Avatar not found. Set AVATAR_ID and VOICE_ID in your server .env with the avatar and voice IDs from your LiveAvatar/HeyGen dashboard, then restart the server.');
      } else if (msg.toLowerCase().includes('internal server error') || msg.includes('500') || msg.includes('API request failed')) {
        setErrorType('GENERAL');
        const serverReason = msg ? `"${msg.trim()}"` : 'Server error';
        setErrorMessage(
          `LiveAvatar: ${serverReason}. This often means exhausted credits, an invalid API key, or a temporary outage. ` +
          'Check your HeyGen/LiveAvatar dashboard for API usage and credits. In Vercel: set REACT_APP_HEYGEN_API_KEY or LIVEAVATAR_API_KEY in Project → Settings → Environment Variables, then redeploy.'
        );
      } else {
        setErrorType('GENERAL');
        setErrorMessage(msg || "Connection failed. Check avatar_id, voice_id, and API key in your LiveAvatar/HeyGen dashboard.");
      }
      
      setSessionActive(false);
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  // Static mode handler
  const handleStaticMode = () => {
    trackButtonClick('archive_mode', { page: 'avatar' });
    pushDataLayerEvent('archive_mode_enabled', { page: 'avatar' });
    setMode('static');
    setIsConnected(true);
    setErrorType(null);
    setSessionActive(true);
  };

  // Initialize browser Speech Recognition API (tuned for desktop when applicable)
  const initializeSpeechRecognition = () => {
    // Chrome uses webkitSpeechRecognition; try webkit first for best compatibility
    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SpeechRecognition) {
      warn('Speech Recognition API not supported in this browser');
      return null;
    }

    const isDesktop = typeof window !== 'undefined' &&
      window.innerWidth >= 768 &&
      !('ontouchstart' in window);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true; // Always on – keeps session alive and reduces no-speech timeouts
    recognition.maxAlternatives = 3; // More alternatives = better chance of matching wake phrase
    // Android Chrome is flaky with non–en-US tags; force en-US for reliable cloud SR.
    recognition.lang = isAndroid
      ? 'en-US'
      : ((typeof navigator !== 'undefined' && /^en(-[A-Za-z]{2})?$/i.test(navigator.language))
        ? navigator.language
        : 'en-US');

    recognition.onstart = () => {
      if (recognitionRef.current !== recognition) return;
      log('Speech recognition started');
      androidSrDebugLog('SR onstart');
      // iOS/desktop: quiet until wake. Android: wake primed at stream ready — show Listening when active.
      setIsListening(Boolean(wakeActiveRef.current));
    };

    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition) return;
      // Silent mute: recognition stays running (no OS mic sounds) but everything heard
      // while muted is discarded here. Tap Unmute to open the gate and speak.
      if (micGateRef.current) return;
      const results = event.results;
      if (!results.length) return;

      const lastResult = results[results.length - 1];

      // Thin wrapper — always delegates to the ref so the latest closures are used.
      const submitWakeQuery = () => submitWakeQueryRef.current?.();

      // Best-effort "user speaking" indicator (used only for UI labels).
      // Any non-empty transcript (interim or final) means user is actively speaking.
      try {
        const anyTxt = String(lastResult?.[0]?.transcript || '').trim();
        if (anyTxt) {
          touchSessionActivity();
          setIsUserSpeaking(true);
          if (userSpeakingTimeoutRef.current) clearTimeout(userSpeakingTimeoutRef.current);
          userSpeakingTimeoutRef.current = setTimeout(() => {
            setIsUserSpeaking(false);
          }, 900);
        }
      } catch (_) {}

      // ── Interim (non-final) results ────────────────────────────────────────
      if (!lastResult.isFinal) {
        // Legacy iOS Safari: allow interruption using interim speech while avatar is speaking.
        if (isLegacyIOSSafari && isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const wordCount = interimText.split(/\s+/).filter(Boolean).length;
          if (interimText && wordCount >= 2) {
            try { stopAvatarNarration(); } catch (_) {}
            const vc = voiceAbortControllerRef.current;
            if (vc) try { vc.abort(); } catch (_) {}
            const cc = chatAbortControllerRef.current;
            if (cc) try { cc.abort(); } catch (_) {}
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1200);
            return;
          }
        }

        // Legacy iOS Safari fallback: accept longer interim phrases as a query because "final"
        // often never arrives on older devices.
        if (isLegacyIOSSafari && !wakeActiveRef.current && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const words = interimText.split(/\s+/).filter(Boolean);
          if (interimText && words.length >= 4) {
            wakeActiveRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1800);
            return;
          }
        }

        // Android Chrome: same as legacy iOS — finals are often missing or very late; interim must carry the query.
        if (isAndroid && isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const wordCount = interimText.split(/\s+/).filter(Boolean).length;
          if (interimText && wordCount >= 2) {
            try { stopAvatarNarration(); } catch (_) {}
            const vc = voiceAbortControllerRef.current;
            if (vc) try { vc.abort(); } catch (_) {}
            const cc = chatAbortControllerRef.current;
            if (cc) try { cc.abort(); } catch (_) {}
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1200);
            return;
          }
        }

        // Android: long interim without finals (same fallback as legacy iOS) if wake not yet set.
        if (isAndroid && !wakeActiveRef.current && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const words = interimText.split(/\s+/).filter(Boolean);
          if (interimText && words.length >= 4) {
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1800);
            return;
          }
        }

        if (wakeActiveRef.current) {
          // Android often ends the SR session before a "final" result; only the interim
          // string exists. If we never copy interim into coalesce, submitWakeQuery fires
          // with an empty merge and the avatar ignores the user.
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const coalesce = wakeCoalesceRef.current;
          if (interimText && !isAvatarTalkingRef.current) {
            const cleanedInterim = interimText.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(president\s+lincoln|lincoln|president)[,\s]*/i, '').trim();
            const toStore = cleanedInterim || interimText;
            if (toStore) coalesce.transcripts = [toStore];
          }
          // User is still speaking — extend submit timer so it never fires mid-sentence.
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = setTimeout(submitWakeQuery, 1500);
          return;
        }
        // Non-Android: detect wake phrase in interim (split utterances, optional greeting).
        if (!isAndroid && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          if (interimText) {
            const interimLower = interimText.toLowerCase();
            const bufPlusInterim = (recentSpeechRef.current + ' ' + interimLower).trim().slice(-300);
            const interimWake = findWakePhrase(interimLower) || findWakePhrase(bufPlusInterim);
            if (interimWake) {
              recentSpeechRef.current = '';
              wakeActiveRef.current = true;
              sessionWakeActivatedRef.current = true;
              isActivelyListeningRef.current = true;
              setIsActivelyListening(true);
              setIsListening(true);
              const after = extractAfterWake(interimText, interimWake);
              if (after) {
                const coalesce = wakeCoalesceRef.current;
                coalesce.transcripts = [after];
                if (coalesce.timer) clearTimeout(coalesce.timer);
                coalesce.timer = setTimeout(submitWakeQuery, 1500);
              } else {
                const greeting = WAKE_GREETINGS[wakeCountRef.current % WAKE_GREETINGS.length];
                wakeCountRef.current += 1;
                makeAvatarSpeak(greeting).catch(() => {});
                const coalesce = wakeCoalesceRef.current;
                if (coalesce.timer) clearTimeout(coalesce.timer);
                coalesce.timer = setTimeout(submitWakeQuery, 7000);
              }
            }
          }
        }
        return;
      }
      // ────────────────────────────────────────────────────────────────────────

      // Collect ALL alternatives (Chrome returns up to maxAlternatives).
      // Using every alternative maximises the chance of catching the wake phrase even
      // when the top-confidence transcript is wrong (e.g. "playing" vs "hey President Lincoln").
      const allAlts = [];
      for (let j = 0; j < lastResult.length; j++) {
        const txt = String(lastResult[j].transcript || '').trim();
        if (txt) allAlts.push(txt);
      }
      if (!allAlts.length) return;

      // Loudness/clarity gate for noisy, crowded rooms: when the engine reports a real
      // confidence score, drop clearly low-confidence finals (distant/overlapping voices)
      // so only clear, nearby speech drives the conversation. Never suppress while the
      // avatar is speaking (that's a barge-in/interruption and must always go through).
      if (MIN_SPEECH_CONFIDENCE > 0 && !isAvatarTalkingRef.current) {
        const topConfidence = Number(lastResult[0]?.confidence);
        if (Number.isFinite(topConfidence) && topConfidence > 0 && topConfidence < MIN_SPEECH_CONFIDENCE) {
          androidSrDebugLog(`SR final dropped (low confidence ${topConfidence.toFixed(2)})`);
          return;
        }
      }

      // Primary transcript = first (highest-confidence) alternative.
      const t = allAlts[0];

      log('Speech recognized:', t, allAlts.length > 1 ? `(+${allAlts.length - 1} alts)` : '');
      androidSrDebugLog(`SR final: ${t.length > 80 ? `${t.slice(0, 80)}…` : t}`);
      lastSpeechResultTimeRef.current = Date.now();
      hasReceivedAnySpeechRef.current = true;
      setVoicePermissionHelpType('both');
      if (isMobile) {
        setVoicePermissionHelpVisible(false);
      }

      // ── Natural barge-in ────────────────────────────────────────────────────
      // While the avatar is speaking, ANY speech of ≥ 2 words interrupts it.
      // This gives a "real conversation" feel — no wake phrase needed to cut in.
      if (isAvatarTalkingRef.current) {
        const wordCount = t.split(/\s+/).filter(Boolean).length;
        if (wordCount >= 2) {
          try { stopAvatarNarration(); } catch (_) {}
          const vc = voiceAbortControllerRef.current;
          if (vc) try { vc.abort(); } catch (_) {}
          const cc = chatAbortControllerRef.current;
          if (cc) try { cc.abort(); } catch (_) {}

          // Android: no wake-word greetings — send question text only (strip optional wake prefix).
          if (isAndroid) {
            let toSend = t.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(president\s+lincoln|lincoln|president)[,\s]*/i, '').trim();
            if (!toSend) {
              for (const alt of allAlts) {
                const w = findWakePhrase(alt);
                if (w) {
                  const after = extractAfterWake(alt, w);
                  if (after) { toSend = after; break; }
                }
              }
            }
            if (!toSend) toSend = t;
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            const coalesce = wakeCoalesceRef.current;
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = null;
            coalesce.transcripts = [];
            recentSpeechRef.current = '';
            handleVoiceInput(toSend, { appendUserMessage: true }).catch((err) => console.error('Barge-in error:', err));
            return;
          }

          // If they also said the wake phrase (e.g. "hey president lincoln what about X"), handle gracefully.
          let bargeWake = null, bargeWakeSource = '';
          for (const alt of allAlts) {
            const w = findWakePhrase(alt);
            if (w) { bargeWake = w; bargeWakeSource = alt; break; }
          }

          if (bargeWake) {
            const after = extractAfterWake(bargeWakeSource, bargeWake);
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            if (after) {
              const coalesce = wakeCoalesceRef.current;
              coalesce.transcripts = [after];
              if (coalesce.timer) clearTimeout(coalesce.timer);
              coalesce.timer = setTimeout(submitWakeQuery, 600);
            } else {
              const bg = WAKE_GREETINGS[wakeCountRef.current % WAKE_GREETINGS.length];
              wakeCountRef.current += 1;
              makeAvatarSpeak(bg).catch(() => {});
              const coalesce = wakeCoalesceRef.current;
              if (coalesce.timer) clearTimeout(coalesce.timer);
              coalesce.timer = setTimeout(submitWakeQuery, 6000);
            }
          } else {
            // Direct barge-in question — no wake phrase required mid-conversation.
            // wakeActiveRef stays true — no need to say "Hey President Lincoln" again after barge-in.
            const coalesce = wakeCoalesceRef.current;
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = null;
            coalesce.transcripts = [];
            recentSpeechRef.current = '';
            handleVoiceInput(t, { appendUserMessage: true }).catch((err) => console.error('Barge-in error:', err));
          }
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Production mode: accept normal speech directly (wake phrase optional).
      if (!wakeActiveRef.current) {
        wakeActiveRef.current = true;
        sessionWakeActivatedRef.current = true;
        isActivelyListeningRef.current = true;
        setIsActivelyListening(true);
        setIsListening(true);
      }

      // Wake already active: add this utterance to the question and (re)start the submit timer.
      // Ignore recognition results that fire while the avatar itself is talking (echo prevention).
      if (isAvatarTalkingRef.current) return;
      let cleaned = t.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(president\s+lincoln|lincoln|president)[,\s]*/i, '').trim();
      // Android: cloud SR sometimes returns only a long mis-heard wake phrase; avoid dropping the whole utterance.
      if (!cleaned && isAndroid && t.replace(/\s+/g, ' ').trim().length >= 8) {
        cleaned = t.replace(/\s+/g, ' ').trim();
      }
      if (!cleaned) return;
      // Turn off the listening ring the moment user speech arrives.
      // Do NOT touch isTalking here — makeAvatarSpeak() may have already set it true
      // for a previous response before AVATAR_SPEAK_STARTED updates isAvatarTalkingRef.
      setIsListening(false);
      const coalesce = wakeCoalesceRef.current;
      const prevJoined = coalesce.transcripts.filter(Boolean).join(' ').trim();
      // Recognition restarts re-transcribe the same utterance from the beginning, so
      // fragments overlap. Merge on the overlap instead of blindly appending —
      // appending is what produced "do you think do you think you would …".
      coalesce.transcripts = [mergeTranscriptFragment(prevJoined, cleaned)];
      if (coalesce.timer) clearTimeout(coalesce.timer);
      // 3 s window: Android SR restarts take ~300 ms + the user may pause mid-sentence.
      // If a second SR session captures more words within 3 s, the timer resets and
      // the full phrase is collected before submission.
      coalesce.timer = setTimeout(submitWakeQuery, 3000);
    };

    recognition.onerror = (event) => {
      // Chrome/Android always fires error "aborted" after recognition.stop() (pause, session end, etc.).
      // It is not a failure — onend runs next and handles cleanup / restart. Do not reset UI here
      // or log as console.error (avoids duplicate state churn with onend).
      if (event.error === 'aborted') {
        log('Recognition aborted (normal — after stop/pause)');
        androidSrDebugLog('SR aborted + onend (expected when pausing SR)');
        return;
      }

      // After recognition.stop(), a replaced instance may still emit errors — ignore stale ones.
      if (recognitionRef.current !== recognition) return;

      // Android: no-speech is routine; onend follows and debounced-restart handles it. Skip console.error noise.
      if (isAndroid && event.error === 'no-speech') {
        log('No speech detected');
        androidSrDebugLog('SR no-speech');
        return;
      }

      console.error('Speech recognition error:', event.error);
      androidSrDebugLog(`SR error: ${event.error || 'unknown'}`);

      // Do NOT call recognition.stop() here — SR ends on its own after an error,
      // triggering onend which handles the restart. Calling stop() manually on Android
      // adds an extra mic-system round-trip, generating duplicate mic on/off sounds.
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);
      setIsListening(false);

      if (event.error === 'no-speech') {
        log('No speech detected');
      } else if (event.error === 'not-allowed') {
        showVoicePermissionHelp('both'); // Resolved by actual mic state: denied → mic popup, granted → SR popup
      } else if (event.error === 'network') {
        if (isDesktop) {
          alert('Voice recognition needs internet. Please check your connection and try again.');
        } else if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
          // Mobile Chrome: transient network blips to Google speech servers are common; retry once.
          setTimeout(() => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            try { startSpeechRecognition(); } catch (_) {}
          }, 900);
        }
      } else if (event.error === 'audio-capture') {
        showVoicePermissionHelp('microphone');
      }
    };

    recognition.onend = () => {
      // A newer SpeechRecognition instance may already be active — do not clear its state or schedule duplicate starts.
      if (recognitionRef.current !== recognition) {
        androidSrDebugLog('SR onend (stale instance, ignored)');
        return;
      }

      log('Speech recognition ended');
      androidSrDebugLog('SR onend');
      setIsListening(false);
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);

      if (!sessionActiveRef.current || isMicMutedRef.current) return;

      // Avatar speaking: non-Android / legacy paths paused SR — wait for SPEAK_ENDED to restart.
      // Android keeps SR for barge-in; if the engine still ends mid-utterance (silence timeout), re-arm SR.
      if (isAvatarTalkingRef.current) {
        if (isAndroid) {
          log('SR onend while avatar speaking — Android debounced rearm for barge-in');
          androidSrDebugLog('SR onend during avatar speech → debounce rearm');
          ensureAndroidKeepAliveHealthy();
          if (androidSrOnEndDebounceTimerRef.current) {
            clearTimeout(androidSrOnEndDebounceTimerRef.current);
          }
          androidSrOnEndDebounceTimerRef.current = setTimeout(() => {
            androidSrOnEndDebounceTimerRef.current = null;
            if (!sessionActiveRef.current || isMicMutedRef.current) return;
            if (isActivelyListeningRef.current) return;
            if (!isAvatarTalkingRef.current) return;
            const go = () => {
              if (!sessionActiveRef.current || isMicMutedRef.current) return;
              if (androidShouldGateSrWithAudioMonitor()) startAudioMonitoring();
              else startSpeechRecognition();
            };
            requestAnimationFrame(() => setTimeout(go, 90));
          }, 420);
          return;
        }
        log('SR onend while avatar speaking — defer restart to AVATAR_SPEAK_ENDED');
        return;
      }

      // Android: debounce rapid onend (silence / internal recycle) so one recognition.start() follows many ends.
      // SR cycling is what drives repeated OS mic enable/disable sounds.
      if (isAndroid) {
        ensureAndroidKeepAliveHealthy();
        if (androidSrOnEndDebounceTimerRef.current) {
          clearTimeout(androidSrOnEndDebounceTimerRef.current);
        }
        androidSrOnEndDebounceTimerRef.current = setTimeout(() => {
          androidSrOnEndDebounceTimerRef.current = null;
          if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
          if (isActivelyListeningRef.current) return;
          const go = () => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            if (androidShouldGateSrWithAudioMonitor()) startAudioMonitoring();
            else startSpeechRecognition();
          };
          requestAnimationFrame(() => setTimeout(go, 90));
        }, 420);
        return;
      }

      // Non-Android: restart SR immediately so wake-word detection keeps working continuously.
      const restartDelay = wakeActiveRef.current ? 0 : 100;

      setTimeout(() => {
        if (!sessionActiveRef.current || isMicMutedRef.current) return;
        if (isActivelyListeningRef.current) return; // Another SR already started (race guard)
        try {
          if (isAndroid) {
            // Fresh object every restart — Android Chrome can't reliably restart a stopped instance.
            recognitionRef.current = initializeSpeechRecognition();
            if (!recognitionRef.current) return;
          }
          activeListeningStartTimeRef.current = Date.now();
          isActivelyListeningRef.current = true;
          setIsActivelyListening(true);
          setIsListening(Boolean(wakeActiveRef.current));
          (isAndroid ? recognitionRef.current : recognition).start();
        } catch (e) {
          isActivelyListeningRef.current = false;
          setIsActivelyListening(false);
          setIsListening(false);
          // Secondary retry with a longer back-off
          const retryDelay = isAndroid ? 400 : 500;
          setTimeout(() => {
            if (!sessionActiveRef.current || isActivelyListeningRef.current || isMicMutedRef.current) return;
            try {
              if (isAndroid) recognitionRef.current = initializeSpeechRecognition();
              if (!recognitionRef.current) return;
              activeListeningStartTimeRef.current = Date.now();
              isActivelyListeningRef.current = true;
              setIsActivelyListening(true);
              setIsListening(Boolean(wakeActiveRef.current));
              recognitionRef.current.start();
            } catch (err) {
              log('Recognition restart failed:', err?.message);
              setIsListening(false);
            }
          }, retryDelay);
        }
      }, restartDelay);
    };

    return recognition;
  };

  const startBrowserSpeechRecognition = () => {
    // Do not start if user has muted the mic
    if (isMicMutedRef.current) {
      androidSrDebugLog('startSR skipped: mic muted');
      return;
    }
    if (!sessionActiveRef.current) {
      androidSrDebugLog('startSR skipped: no session');
      return;
    }
    // If already running, do nothing (we want always-on listening for wake phrase)
    if (isActivelyListeningRef.current) {
      androidSrDebugLog('startSR skipped: already active');
      return;
    }
    ensureAndroidKeepAliveHealthy();

    // On Android, always create a fresh recognition object — stopped instances cannot be restarted
    // reliably on Android Chrome, and VAD may call this after the previous object has ended.
    if (isAndroid || !recognitionRef.current) {
      recognitionRef.current = initializeSpeechRecognition();
    }

    if (!recognitionRef.current) {
      androidSrDebugLog('startSR failed: SpeechRecognition API missing');
      // No alert(): this runs on every restart, so in browsers without speech recognition
      // (Firefox, Instagram/Facebook in-app browsers) it raised one blocking popup after
      // another, pointing phone visitors to a text box phones do not have. They can ask
      // with the starter questions instead.
      warn('Speech recognition is not supported in this browser; voice input is off.');
      return;
    }

    try {
      androidSrDebugLog('SR recognition.start() called');
      activeListeningStartTimeRef.current = Date.now();
      isActivelyListeningRef.current = true;
      setIsActivelyListening(true);
      setIsListening(Boolean(wakeActiveRef.current)); // Quiet wake listening unless wake is active
      recognitionRef.current.start();
    } catch (error) {
      androidSrDebugLog(`SR start() threw: ${error?.message || error}`);
      console.error('Error starting speech recognition:', error);
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);
      setIsListening(false);
      if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
        recognitionRef.current = null;
        setTimeout(() => {
          if (!sessionActiveRef.current || isMicMutedRef.current || isActivelyListeningRef.current) return;
          if (isAvatarTalkingRef.current) return;
          try { startBrowserSpeechRecognition(); } catch (_) {}
        }, 620);
      }
    }
  };

  const startSpeechRecognition = () => {
    startBrowserSpeechRecognition();
  };

  // Stop speech recognition. soft=true keeps mic stream alive to reduce access sounds.
  const stopSpeechRecognition = (soft = false) => {
    stopAudioMonitoring(); // cancel any pending audio-level poll
    if (androidSrOnEndDebounceTimerRef.current) {
      clearTimeout(androidSrOnEndDebounceTimerRef.current);
      androidSrOnEndDebounceTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        // Stop recognition immediately
        recognitionRef.current.stop();
      } catch (error) {
        // Ignore errors if already stopped or not started
        log('Recognition stop attempt:', error.message);
      }
    }
    // Always reset SR UI state; soft stop keeps wake/coalesce so watchdog/mute does not break mid-session listening.
    isActivelyListeningRef.current = false;
    setIsActivelyListening(false);
    setIsListening(false);
    setIsUserSpeaking(false);
    const wc = wakeCoalesceRef.current;
    if (wc.timer) clearTimeout(wc.timer);
    wc.timer = null;
    wc.transcripts = [];
    if (!soft) {
      wakeActiveRef.current = false;
      sessionWakeActivatedRef.current = false;
      recentSpeechRef.current = '';
    }
  };

  // Toggle mic mute so avatar does not capture background conversations
  const handleToggleMicMute = () => {
    trackButtonClick('toggle_mic_mute', { page: 'avatar' });
    if (isMicPermissionDenied || !hasMicDevice) return;
    const next = !micGateRef.current;
    micGateRef.current = next;
    setIsMicMuted(next);
    // A manual tap is the user's explicit choice: it overrides auto-mute. Unmuting here
    // (even mid-speech) lets them interrupt; muting here keeps the mic off after speech.
    manualMuteRef.current = next;
    autoMutedForSpeechRef.current = false;
    // isMicMutedRef stays false on purpose: recognition keeps cycling in the background
    // (a silent gate discards results) so the OS never plays mic on/off sounds.

    if (next) {
      // Drop any half-captured speech so it isn't submitted after unmute.
      const coalesce = wakeCoalesceRef.current;
      if (coalesce.timer) clearTimeout(coalesce.timer);
      coalesce.timer = null;
      coalesce.transcripts = [];
      recentSpeechRef.current = '';
    } else if (sessionActiveRef.current && !isActivelyListeningRef.current) {
      // Recognition normally never stopped; restart only if it actually died.
      try {
        androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
      } catch (_) {}
    }
  };
  const isMicOff = isMicMuted || isMicPermissionDenied || !hasMicDevice;
  // While Lincoln speaks the mic is auto-muted; "Tap to unmute" read to new visitors as
  // "your microphone is off". Tapping mid-speech lets them talk over him, so say so.
  const micButtonLabel = !hasMicDevice || isMicPermissionDenied
    ? 'Unavailable'
    : (isMicMuted ? (isTalking ? 'Tap to interrupt' : 'Tap to unmute') : '');

  // Every question gets a spoken answer. When no reply can be fetched - connection lost,
  // server unreachable - Lincoln says a short in-character line instead of going silent
  // or showing an error the visitor cannot act on.
  const speakFallbackLine = () => {
    const line = pickLincolnFallback();
    addChatMessage(line, 'avatar');
    makeAvatarSpeak(line).catch((err) => console.error('Error making avatar speak fallback:', err));
  };

  // Handle voice input (same as typed message)
  const handleVoiceInput = async (text, options = {}) => {
    const { appendUserMessage = false } = options;
    if (!text || !text.trim()) return;
    if (!sessionActiveRef.current) return;
    // Final guard for every voice path (including barge-in, which fires while the
    // avatar is speaking): never treat the avatar's own voice as a user question.
    if (looksLikeAvatarEcho(text, recentAvatarSpeechRef.current)) {
      androidSrDebugLog('handleVoiceInput: dropped avatar echo');
      return;
    }
    if (shouldSkipDuplicateSubmit(text)) return;
    if (isResponseInFlightRef.current) return;
    if (appendUserMessage) addChatMessage(text, 'user');
    isResponseInFlightRef.current = true;
    if (isGoodbyeMessage(text)) {
      pendingGoodbyeDisconnectRef.current = true;
    } else {
      pendingGoodbyeDisconnectRef.current = false;
    }
    pushDataLayerEvent('voice_query_submitted', {
      page: 'avatar',
      text_length: String(text.trim().length),
      is_goodbye: Boolean(isGoodbyeMessage(text)),
    });

    let timeoutId = null;
    let timedOut = false;
    try {
      setIsThinking(true);

      const controller = new AbortController();
      voiceAbortControllerRef.current = controller;
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, CHAT_CLIENT_TIMEOUT_MS);

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: chatMessages.slice(-10).map(msg => ({
            role: msg.role === 'user' ? 'user' : 'model',
            text: msg.content
          })),
          provider: 'auto'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      voiceAbortControllerRef.current = null;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      let aiResponse = data.response;

      if (!aiResponse || !aiResponse.trim()) {
        console.error('❌ AI response is empty or invalid');
        speakFallbackLine();
        return;
      }

      addChatMessage(aiResponse, 'avatar');
      pushDataLayerEvent('voice_reply_received', {
        page: 'avatar',
        reply_length: String(aiResponse.trim().length),
      });
      makeAvatarSpeak(aiResponse).catch((err) => {
        console.error('Error making avatar speak:', err);
      });
    } catch (error) {
      console.error('Voice input error:', error);
      pendingGoodbyeDisconnectRef.current = false;
      // The visitor interrupted: stay quiet, a new question is coming.
      if (error.name === 'AbortError' && !timedOut) return;
      pushDataLayerEvent('voice_query_failed', {
        page: 'avatar',
        error_name: String(timedOut ? 'Timeout' : (error?.name || 'unknown')),
        error_message: String(error?.message || 'unknown'),
      });
      speakFallbackLine();
    } finally {
      clearTimeout(timeoutId);
      voiceAbortControllerRef.current = null;
      isResponseInFlightRef.current = false;
      // If speech is about to start, AVATAR_SPEAK_STARTED will clear Thinking.
      if (!isAvatarTalkingRef.current) setIsThinking(false);
    }
  };

  // Always-current submit function — updated every render so VAD and SR closures stay in sync.
  // Call via submitWakeQueryRef.current() from any callback (VAD, SR, timers).
  submitWakeQueryRef.current = () => {
    const coalesce = wakeCoalesceRef.current;
    if (coalesce.timer) clearTimeout(coalesce.timer);
    coalesce.timer = null;
    // Muted (silent gate): drop anything captured before the mute took effect.
    if (micGateRef.current) {
      coalesce.transcripts = [];
      return;
    }
    const rawMerged = coalesce.transcripts.filter(Boolean).join(' ').trim();
    // Safety net: collapse any repetition that survived merging (background chatter,
    // rapid recogniser restarts) so the AI and the saved transcript stay readable.
    const merged = sanitizeTranscriptForChat(rawMerged).text;
    coalesce.transcripts = [];
    // Speaker echo: the mic can capture the avatar's own voice (especially on speakers
    // without headphones). Without this the bot answers itself in a loop.
    if (merged && looksLikeAvatarEcho(merged, recentAvatarSpeechRef.current)) {
      androidSrDebugLog('submitWakeQuery: dropped avatar echo');
      setIsUserSpeaking(false);
      setIsListening(Boolean(wakeActiveRef.current));
      return;
    }
    setIsUserSpeaking(false);
    // Wake-once-per-session: keep wakeActiveRef = true so the user never needs to
    // say "Hey President Lincoln" again. AVATAR_SPEAK_ENDED will restore isListening after each reply.
    setIsListening(false); // ring off while AI is processing / avatar is about to speak
    if (!merged) {
      androidSrDebugLog('submitWakeQuery: empty merged → no chat send');
      // Empty timer (e.g. 7 s after greeting): keep UX + mic path alive like iOS continuous SR.
      if (wakeActiveRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
        setIsListening(true);
        if (!isActivelyListeningRef.current) {
          setTimeout(() => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
          }, 0);
        }
      }
      return;
    }
    androidSrDebugLog(`submitWakeQuery → chat (${merged.length} chars)`);
    handleVoiceInput(merged, { appendUserMessage: true }).catch((err) => console.error('Voice query error:', err));
  };

  // Handle "Session not found" from LiveAvatar SDK – show message and allow reconnect
  const handleSessionNotFound = (err) => {
    const msg = err?.message || String(err);
    if (/session not found|Session not found/i.test(msg)) {
      saveSessionAsEndedRef.current?.();
      setErrorMessage('Session expired or invalid. Please click Stop then Start again.');
      setIsConnected(false);
      isConnectedRef.current = false;
      setSessionActive(false);
      return true;
    }
    return false;
  };

  // Make avatar speak using LiveAvatar's own voice
  const makeAvatarSpeak = async (aiResponse) => {
    if (!avatarInstanceRef.current) {
      console.error('❌ Avatar session not available');
      return;
    }

    const session = avatarInstanceRef.current;
    
    // Wait for session to be CONNECTED and stream ready (avoids "Session not found" from speaking too early)
    let retries = 0;
    while (session.state !== SessionState.CONNECTED && retries < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    if (session.state !== SessionState.CONNECTED) {
      console.error('❌ Session not in CONNECTED state:', session.state);
      return;
    }
    retries = 0;
    while (!isStreamReadyRef.current && retries < 100) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    if (!isStreamReadyRef.current) {
      console.warn('⚠️ Stream not ready yet; speaking anyway (may get Session not found)');
    }

    const sessionId = sessionIdRef.current || session._sessionInfo?.session_id || session.sessionId;
    // sessionId only needed for /api/task fallback – don't block TTS or repeat()

    try {
      setIsTalking(true);
      const text = aiResponse.trim();
      if (!text) return;
      // repeat() = AVATAR_SPEAK_TEXT (avatar speaks this text); message() = AVATAR_SPEAK_RESPONSE
      if (typeof session.repeat === 'function') {
        try {
          session.repeat(text);
          return;
        } catch (repeatError) {
          console.error('[Avatar] repeat() failed:', repeatError?.message, repeatError);
          if (handleSessionNotFound(repeatError)) return;
        }
      }
      if (typeof session.message === 'function') {
        try {
          session.message(text);
          return;
        } catch (msgError) {
          console.error('[Avatar] message() failed:', msgError?.message, msgError);
          if (handleSessionNotFound(msgError)) return;
        }
      }
      if (sessionId) {
        try {
          const taskResponse = await fetch('/api/task', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId, text, task_type: 'talk' }),
          });
          if (taskResponse.ok) return;
          console.warn('[Avatar] /api/task non-ok:', taskResponse.status);
        } catch (apiError) {
          console.error('[Avatar] /api/task error:', apiError?.message, apiError);
        }
      }
      console.error('[Avatar] All speech methods failed – avatar may not speak');
      setIsTalking(false);
    } catch (speakError) {
      console.error('[Avatar] Error making avatar speak:', speakError?.message, speakError);
      setIsTalking(false);
    }
  };

  // Show voice permission help popup – single "both" message for clarity (one set of steps for all users)
  const showVoicePermissionHelp = (type) => {
    if (voicePermissionHelpShownRef.current) return;
    if (Date.now() - lastMicGrantedAtRef.current < 20000) return;
    // Audio monitor running means we are actively watching for speech — not stuck.
    if (isAndroid && audioMonitorRef.current) return;
    if (type === 'microphone' || micPermissionStateRef.current === 'denied') {
      micPermissionStateRef.current = 'denied';
      setIsMicPermissionDenied(true);
    }
    voicePermissionHelpShownRef.current = true;
    setVoicePermissionHelpType('both');
    setVoicePermissionHelpVisible(true);
  };

  // Send message handler - Uses AI for responses then speaks via LiveAvatar
  const handleSendMessage = async (e, presetText) => {
    e?.preventDefault();

    const messageText = String(presetText ?? chatInput).trim();
    if (!messageText || !sessionActive) return;
    if (shouldSkipDuplicateSubmit(messageText)) return;
    if (isResponseInFlightRef.current) return;
    isResponseInFlightRef.current = true;
    if (isGoodbyeMessage(messageText)) {
      pendingGoodbyeDisconnectRef.current = true;
    } else {
      pendingGoodbyeDisconnectRef.current = false;
    }
    pushDataLayerEvent('text_query_submitted', {
      page: 'avatar',
      text_length: String(messageText.length),
      is_goodbye: Boolean(isGoodbyeMessage(messageText)),
    });
    addChatMessage(messageText, 'user');
    if (presetText == null) setChatInput('');
    setIsThinking(true);

    const historyWithNew = [
      ...chatMessages.map(msg => ({ role: msg.role === 'user' ? 'user' : 'model', text: msg.content })),
    ].slice(-10);

    const isFirstQuestion = chatMessages.filter(msg => msg.role === 'assistant').length === 0;

    const controller = new AbortController();
    chatAbortControllerRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CHAT_CLIENT_TIMEOUT_MS);

    try {
      // Full response then speak via LiveAvatar
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, history: historyWithNew, provider: 'auto' }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      let aiResponse = data.response;

      if (!aiResponse || !aiResponse.trim()) {
        console.error('❌ AI response is empty or invalid');
        speakFallbackLine();
        return;
      }

      if (isFirstQuestion) {
        aiResponse = "My fellow citizens, I greet you from across time. Bring forth your questions, and together we shall seek clarity, truth, and thoughtful counsel.";
      }

      addChatMessage(aiResponse, 'avatar');
      pushDataLayerEvent('text_reply_received', {
        page: 'avatar',
        reply_length: String(aiResponse.trim().length),
      });
      makeAvatarSpeak(aiResponse).catch((err) => {
        console.error('Error making avatar speak (text input):', err);
      });
      
    } catch (error) {
      pendingGoodbyeDisconnectRef.current = false;
      // The visitor interrupted: stay quiet, a new question is coming.
      if (error.name === 'AbortError' && !timedOut) return;
      pushDataLayerEvent('text_query_failed', {
        page: 'avatar',
        error_name: String(timedOut ? 'Timeout' : (error?.name || 'unknown')),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Message send error:', error);
      speakFallbackLine();
    } finally {
      clearTimeout(timeoutId);
      chatAbortControllerRef.current = null;
      isResponseInFlightRef.current = false;
      setIsThinking(false);
    }
  };

  // Save chat history — each call upserts Supabase; Google Sheets only when endedAt is set
  const saveChatHistory = async (messages, sessionId, startedAt, endedAt) => {
    const list = Array.isArray(messages) ? messages : [];
    const started = startedAt || sessionStartTimeRef.current || new Date().toISOString();
    try {
      const res = await fetch('/api/chat-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || `local-${Date.now()}`,
          startedAt: started,
          endedAt: endedAt != null ? endedAt : null,
          avatarName: SESSION_SAVE_NAME,
          userEmail: userEmailRef.current || normalizeEmail(emailInput),
          messages: list.map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp || null,
          })),
        }),
      });
      if (!res.ok) {
        const errText = await res.text();
        warn('Chat history save failed:', res.status, errText?.slice(0, 200));
        return false;
      }
      return true;
    } catch (e) {
      warn('Could not save chat history:', e?.message || e);
      return false;
    }
  };

  // The avatar connection dropped (phone locked, network lost, session expired) without the
  // visitor ending the chat. Going inactive also stops the idle timer and the tab-close
  // beacon, so unless the conversation is saved as ended here, nothing ever closes it.
  const saveSessionAsEnded = () => {
    if (hasSavedThisSessionRef.current || isClosingRef.current) return;
    const sid = sessionIdRef.current;
    const started = sessionStartTimeRef.current;
    if (!sid || !started || !chatMessagesRef.current?.length) return;
    hasSavedThisSessionRef.current = true;
    const ended = new Date().toISOString();
    chatHistorySaveChainRef.current = chatHistorySaveChainRef.current
      .then(() => saveChatHistory(chatMessagesRef.current, sid, started, ended))
      .catch(() => {});
  };
  saveSessionAsEndedRef.current = saveSessionAsEnded;

  // When session ends (active → inactive), clear chat input and release microphone access
  useEffect(() => {
    const wasActive = prevSessionActiveRef.current;
    prevSessionActiveRef.current = sessionActive;
    if (wasActive && !sessionActive) {
      stopSpeechRecognition(true); // Soft: keep mic stream to reduce re-prompt on next session
      setChatInput('');
      setVoicePermissionHelpType('both');
      setVoicePermissionHelpVisible(false);
    }
  }, [sessionActive]);

  // Typing in the chat box counts as activity (so idle timeout does not fire mid-compose)
  useEffect(() => {
    if (!sessionActive || !chatInput.trim()) return undefined;
    const t = setTimeout(touchSessionActivity, 400);
    return () => clearTimeout(t);
  }, [sessionActive, chatInput, touchSessionActivity]);

  // Realtime save after each new message → database only (Sheets on session end)
  useEffect(() => {
    if (!sessionActive || chatMessages.length === 0 || isClosingRef.current) return undefined;
    if (hasSavedThisSessionRef.current) return undefined;
    const count = chatMessages.length;
    if (count <= lastPersistedMessageCountRef.current) return undefined;
    lastPersistedMessageCountRef.current = count;

    const sid = sessionIdRef.current;
    const started = sessionStartTimeRef.current;
    if (!sid || !started) return undefined;

    chatHistorySaveChainRef.current = chatHistorySaveChainRef.current
      .then(async () => {
        if (isClosingRef.current || hasSavedThisSessionRef.current) return;
        await saveChatHistory(chatMessagesRef.current, sid, started, null);
      })
      .catch(() => {});

    return undefined;
  }, [sessionActive, chatMessages]);

  // Close session handler — final save with endedAt (DB + Google Sheets when configured)
  const handleClose = async (options = {}) => {
    const { reason = 'manual' } = options;
    if (isClosingRef.current) return;
    pushDataLayerEvent('session_end_attempt', { page: 'avatar', end_reason: reason });
    isClosingRef.current = true;
    setIsEndingSession(true);
    // Hide the mic-permission help popup while we tear the session down.
    setVoicePermissionHelpVisible(false);
    // Never let a hung save or avatar.stop() leave "Ending session…" on screen forever.
    const withTimeout = (p, ms) =>
      Promise.race([
        Promise.resolve(p).catch(() => undefined),
        new Promise((resolve) => setTimeout(() => resolve(undefined), ms)),
      ]);
    try {
      // Mark session inactive immediately so mic auto-restart logic stops
      sessionActiveRef.current = false;

      // Immediately stop any ongoing avatar speech
      stopAvatarNarration();

      // Stop speech recognition and release microphone access
      stopSpeechRecognition();
      // Release Android keep-alive mic stream
      stopMicKeepAlive();
      // Reset greeting rotation counter for the next session
      wakeCountRef.current = 0;

      const sid = sessionIdRef.current;
      const started = sessionStartTimeRef.current;
      const ended = new Date().toISOString();
      if (!hasSavedThisSessionRef.current && sid && started) {
        await withTimeout(chatHistorySaveChainRef.current, 4000);
        const saved = await withTimeout(
          saveChatHistory(chatMessagesRef.current, sid, started, ended),
          6000
        );
        if (saved) hasSavedThisSessionRef.current = true;
      }

      if (reason === 'idle') {
        setSessionEndNotice(
          'Session ended after 3 minutes of inactivity. Your conversation was saved.'
        );
      }

      if (avatarInstanceRef.current) {
        await withTimeout(avatarInstanceRef.current.stop(), 5000);
      }
      setSessionActive(false);
      setIsConnected(false);
      isConnectedRef.current = false;
      setMode(null);
      setChatMessages([]);
      sessionStartTimeRef.current = null;
      setFeedbackSessionId(sid || null);
      setFeedbackModalOpen(true);
      pushDataLayerEvent('session_end_success', { page: 'avatar', end_reason: reason });
    } catch (error) {
      pushDataLayerEvent('session_end_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Session close error:', error);
    } finally {
      setIsEndingSession(false);
      isClosingRef.current = false;
    }
  };

  // Keep handleCloseRef updated so AVATAR_SPEAK_ENDED can call it for goodbye auto-disconnect
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  // Auto-end session after 3 minutes of idle → handleClose saves DB + Google Sheets
  useEffect(() => {
    if (!sessionActive) return undefined;

    touchSessionActivity();

    const intervalId = setInterval(() => {
      if (!sessionActiveRef.current || isClosingRef.current) return;
      if (
        isAvatarTalkingRef.current
        || isResponseInFlightRef.current
        || isThinkingRef.current
      ) {
        touchSessionActivity();
        return;
      }
      const idleMs = Date.now() - lastSessionActivityAtRef.current;
      if (idleMs >= SESSION_IDLE_MS) {
        pushDataLayerEvent('session_idle_timeout', {
          page: 'avatar',
          idle_seconds: Math.round(idleMs / 1000),
        });
        handleCloseRef.current?.({ reason: 'idle' });
      }
    }, SESSION_IDLE_CHECK_MS);

    return () => clearInterval(intervalId);
  }, [sessionActive, touchSessionActivity]);

  // A visitor who just shuts the tab never reaches handleClose, so ended_at stayed NULL
  // and the daily transcript email skipped the conversation for good. pagehide is the one
  // exit event that still fires on mobile Safari, and sendBeacon outlives the page.
  useEffect(() => {
    if (!sessionActive) return undefined;

    const closeOnExit = () => {
      if (hasSavedThisSessionRef.current || isClosingRef.current) return;
      const sid = sessionIdRef.current;
      const started = sessionStartTimeRef.current;
      const messages = chatMessagesRef.current;
      if (!sid || !started || !messages?.length) return;
      if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;

      const body = JSON.stringify({
        sessionId: sid,
        startedAt: started,
        endedAt: new Date().toISOString(),
        avatarName: SESSION_SAVE_NAME,
        userEmail: userEmailRef.current || normalizeEmail(emailInput),
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp || null,
        })),
      });
      const queued = navigator.sendBeacon(
        '/api/chat-history',
        new Blob([body], { type: 'application/json' })
      );
      if (queued) hasSavedThisSessionRef.current = true;
    };

    window.addEventListener('pagehide', closeOnExit);
    return () => window.removeEventListener('pagehide', closeOnExit);
  }, [sessionActive, emailInput]);

  // Cleanup on unmount – release avatar and mic stream
  useEffect(() => {
    return () => {
      if (avatarAudioRecoveryTimeoutRef.current) {
        clearTimeout(avatarAudioRecoveryTimeoutRef.current);
        avatarAudioRecoveryTimeoutRef.current = null;
      }
      if (avatarInstanceRef.current && sessionActive) {
        avatarInstanceRef.current.stop().catch(() => {});
      }
      stopMicKeepAlive();
    };
  }, [sessionActive]);

  // WebView: force inline playback so video doesn't go fullscreen (iOS/Android app WebView)
  useEffect(() => {
    const t = setTimeout(() => {
      const el = videoRef.current;
      if (el) {
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Re-attach video when session becomes active (ensures avatar shows if stream was ready before video ref)
  useEffect(() => {
    if (!sessionActive || !avatarInstanceRef.current || !videoRef.current) return;
    const session = avatarInstanceRef.current;
    try {
      session.attach(videoRef.current);
      if (videoRef.current.paused) {
        videoRef.current.play().then(() => {
          setVideoReady(true);
        }).catch(() => {});
      } else {
        setVideoReady(true);
      }
    } catch (e) {
      warn('Video re-attach:', e?.message);
    }
  }, [sessionActive]);

  // Keep-alive not required for default FULL mode; LiveAvatar manages session lifetime.

  // Auto-scroll chat within the messages container only (no page/body scroll)
  useEffect(() => {
    const el = chatMessagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages]);

  // Stop avatar narration (TTS audio + HeyGen interrupt) – e.g. when user clicks mic
  const stopAvatarNarration = () => {
    const audio = ttsAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (_) {}
      ttsAudioRef.current = null;
    }
    setIsTalking(false);
    isAvatarTalkingRef.current = false;
    if (videoRef.current) {
      videoRef.current.muted = false;
      if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
      try {
        const playPromise = videoRef.current.play?.();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (_) {}
    }
    const session = avatarInstanceRef.current;
    if (session && typeof session.interrupt === 'function') {
      try {
        session.interrupt();
      } catch (_) {}
    }
  };

  // Siri-style interaction: wake phrase handles barge-in and listening (no tap-to-interrupt UI)

  // Handle send button
  const handleSend = () => {
    if (chatInput.trim() && sessionActive && !isTalking) {
      trackButtonClick('send_message', { page: 'avatar', input_method: 'button_or_enter' });
      handleSendMessage();
    }
  };

  const handleStarterQuestion = (question) => {
    if (!sessionActive || isTalking || isThinking) return;
    trackButtonClick('starter_question', { page: 'avatar', question });
    handleSendMessage(null, question);
  };
  const showStarterQuestions = sessionActive && !isEndingSession
    && !chatMessages.some((m) => m.role === 'user');

  // Map messages for display (convert role to match design expectations)
  const messages = chatMessages.map(msg => ({
    ...msg,
    role: msg.role === 'assistant' ? 'assistant' : 'user'
  }));

  // On mobile, pin layout to visual viewport so it doesn't shift when keyboard opens.
  // Desktop uses normal layout flow.
  const layoutStyle = isMobile ? visualViewportStyle : undefined;
  const canAcceptLegal = legalConsentChecked && isValidEmail(emailInput);
  const handleLegalAgree = () => {
    const nextEmail = normalizeEmail(emailInput);
    if (!isValidEmail(nextEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!legalConsentChecked) return;
    trackButtonClick('legal_agree_start', { page: 'avatar' });
    try {
      window.localStorage.setItem('afterlife-bot-legal-accepted', 'true');
      window.localStorage.setItem('afterlife-bot-user-email', nextEmail);
    } catch (e) {}
    userEmailRef.current = nextEmail;
    setEmailInput(nextEmail);
    setEmailError('');
    setShowLegalBeforeStart(false);
    pendingStartRef.current = false;
    // Once the user accepts the terms and provides an email, start the session automatically.
    handleStart();
  };

  return (
    <div className={`avatar-layout flex flex-col h-[100dvh] max-h-[100dvh] w-full max-w-[100vw] min-w-0 bg-white md:bg-slate-100 overflow-hidden ${isMobile ? 'touch-none overscroll-none' : ''}`} style={layoutStyle}>
      {/* ── Apple-Intelligence glow ring (SVG-based, single element, works on Android) ──
           Old approach: div + CSS conic-gradient + mask-composite → broke on Android Chrome
           (mask failed silently → solid colour overlay covering the avatar = "double glow").
           SVG <rect> strokes ARE rings by nature — no masking needed.
           Speaking: rainbow hue-spin. Listening: solid sky-blue pulse. */}
      {isMobile && isTalking && (
        <svg
          className="glow-ring-speak"
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'fixed', inset: 0, width: '100%', height: '100dvh', pointerEvents: 'none', zIndex: 10000, overflow: 'visible' }}
        >
          <defs>
            <filter id="gr-bloom-speak" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur" />
            </filter>
          </defs>
          {/* Soft bloom – thick blurred stroke */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(90,200,250,0.65)" strokeWidth="14"
            rx="2.2" vectorEffect="non-scaling-stroke"
            filter="url(#gr-bloom-speak)" />
          {/* Sharp ring */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(90,200,250,1)" strokeWidth="5"
            rx="2.2" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
      {isMobile && isListening && !isTalking && (
        <svg
          className="glow-ring-listen"
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'fixed', inset: 0, width: '100%', height: '100dvh', pointerEvents: 'none', zIndex: 10000, overflow: 'visible' }}
        >
          <defs>
            <filter id="gr-bloom-listen" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur" />
            </filter>
          </defs>
          {/* Soft bloom */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(56,189,248,0.60)" strokeWidth="12"
            rx="2.2" vectorEffect="non-scaling-stroke"
            filter="url(#gr-bloom-listen)" />
          {/* Sharp ring */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(56,189,248,1)" strokeWidth="4"
            rx="2.2" vectorEffect="non-scaling-stroke" />
        </svg>
      )}

      {/* User journey / onboarding – first-time app intro */}
      {showUserJourney && (
        <UserJourney
          mode={userJourneyMode}
          onDismiss={() => setShowUserJourney(false)}
        />
      )}
      {/* Legal disclaimer – show every time before starting session */}
      {showLegalBeforeStart && createPortal(
        <div
          className={`legal-disclaimer-overlay${legalKeyboardOpen ? ' legal-disclaimer-overlay--keyboard' : ''}`}
          style={legalShellStyle ? { ...legalShellStyle, position: 'fixed' } : undefined}
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-before-start-title"
        >
          <div
            className="legal-disclaimer-modal relative"
            style={{
              maxHeight: legalViewport
                ? `min(${legalViewport.height - 16}px, 88dvh)`
                : undefined,
            }}
          >
            {/* No close button: providing an email and accepting the terms is required
                before a session can start, so the gate must not be dismissable. */}
            <div ref={legalModalScrollRef} className="legal-disclaimer-modal__body">
              <h2 id="legal-before-start-title" className="legal-disclaimer-modal__title">
                Legal disclaimer
              </h2>
              <p className="legal-disclaimer-modal__text">{DISCLAIMER_TEXT}</p>
              <div className="legal-disclaimer-modal__field">
                <label htmlFor="session-email">Email address</label>
                <input
                  ref={emailInputRef}
                  id="session-email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onFocus={() => scrollFieldAboveKeyboard(emailInputRef.current, legalModalScrollRef.current)}
                  onBlur={(e) => setEmailInput(normalizeEmail(e.target.value))}
                  placeholder="you@example.com"
                  autoComplete="email"
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? 'session-email-error' : undefined}
                />
                {emailError && (
                  <div id="session-email-error" className="legal-disclaimer-modal__error">
                    {emailError}
                  </div>
                )}
              </div>
              <label className="legal-disclaimer-modal__consent">
                <input
                  type="checkbox"
                  checked={legalConsentChecked}
                  onChange={(e) => setLegalConsentChecked(e.target.checked)}
                />
                <span>
                  I agree to the <a href="/terms-of-service">Terms of Service</a> and{' '}
                  <a href="/privacy-policy">Privacy Policy</a>.
                </span>
              </label>
            </div>
            <div className="legal-disclaimer-modal__footer">
              <button
                type="button"
                onClick={handleLegalAgree}
                className="legal-disclaimer-modal__button"
                aria-label="I agree to the disclaimer"
                disabled={!canAcceptLegal}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {isEndingSession && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/80">
          <div className="w-10 h-10 border-2 border-white/70 border-t-transparent rounded-full animate-spin mb-3" />
          <div className="text-white text-sm font-medium">Ending session…</div>
          <div className="text-white/70 text-xs mt-1">Saving your conversation…</div>
        </div>
      )}
      {sessionEndNotice && !sessionActive && (
        <div
          className="fixed left-0 right-0 z-[9997] px-4 py-3 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm text-center"
          style={{ top: 'max(3.5rem, calc(env(safe-area-inset-top) + 3rem))' }}
          role="status"
        >
          <span>{sessionEndNotice}</span>
          <button
            type="button"
            className="ml-2 underline font-medium"
            onClick={() => {
              setSessionEndNotice('');
              openFeedbackModal();
            }}
          >
            Leave feedback
          </button>
          <button
            type="button"
            className="ml-2 underline font-medium"
            onClick={() => setSessionEndNotice('')}
          >
            Dismiss
          </button>
        </div>
      )}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={closeFeedbackModal}
        sessionId={feedbackSessionId}
        userEmail={userEmailRef.current || normalizeEmail(emailInput)}
      />
      {/* Header: fixed at top; high z-index so visible in app WebView above video */}
      <header className="avatar-app-header flex-shrink-0 z-[9998] fixed left-0 right-0 top-0 flex items-center justify-center gap-2 sm:gap-5 md:gap-0 py-1.5 sm:py-4 md:py-3 px-3 pt-[max(0.375rem,env(safe-area-inset-top))] sm:pt-4 sm:px-6 md:px-10 bg-white border-b border-slate-200 md:grid md:grid-cols-[200px_1fr_200px] md:items-center overflow-visible">
        <img
          src={LOGO_URL}
          alt="Afterlife Bot"
          className="w-[120px] sm:w-[150px] md:w-[200px] h-auto flex-shrink-0 object-contain rounded md:justify-self-start"
        />
        <div className="text-center min-w-0 flex-1 overflow-hidden md:justify-self-center">
          <h1 className="text-base sm:text-lg md:text-xl font-serif text-slate-800 tracking-wider break-words line-clamp-2">
            {APP_NAME}
          </h1>
          <p className="text-[0.5em] sm:text-[0.62em] md:text-[0.72em] text-slate-500 uppercase tracking-[0.08em] sm:tracking-[0.12em] font-light whitespace-nowrap">
            {APP_SUBTITLE}
          </p>
        </div>
        <div className="relative flex flex-shrink-0 items-center gap-1 md:justify-self-end" ref={headerMenuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setHeaderMenuOpen((open) => {
                const nextOpen = !open;
                if (nextOpen && headerMenuRef.current) {
                  const rect = headerMenuRef.current.getBoundingClientRect();
                  setHeaderMenuPosition({
                    top: rect.bottom + 4,
                    right: Math.max(8, window.innerWidth - rect.right),
                  });
                }
                return nextOpen;
              });
            }}
            className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-sky-600 hover:bg-sky-50"
            aria-label="Help menu"
            aria-expanded={headerMenuOpen}
            aria-haspopup="menu"
          >
            <Info className="w-5 h-5" />
          </button>
          {headerMenuOpen && headerMenuPosition && createPortal(
            <div
              className="fixed min-w-[11rem] py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-[10002]"
              style={{ top: headerMenuPosition.top, right: headerMenuPosition.right }}
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  trackButtonClick('open_user_journey', { page: 'avatar' });
                  setUserJourneyMode('full');
                  setShowUserJourney(true);
                  setHeaderMenuOpen(false);
                }}
              >
                How to use this app
              </button>
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={openFeedbackModal}
              >
                Send feedback
              </button>
            </div>,
            document.body
          )}
        </div>
      </header>

      {/* Spacer so content starts below fixed header (compact on mobile to maximize avatar, no bottom gap) */}
      <div className="flex-shrink-0 h-[3rem] md:h-[5.5rem]" aria-hidden="true" />
      {/* Main content: video | chat – fills to bottom on mobile, no white space */}
      <div className="desktop-main-shell flex-1 flex flex-col md:flex-row md:items-stretch min-h-0 min-w-0 overflow-hidden pt-0 md:pt-3 max-md:pb-0 md:mx-auto md:mt-2 md:mb-3 md:w-[min(1280px,calc(100%-2rem))] md:p-4 md:rounded-2xl md:border md:border-slate-200 md:bg-white md:shadow-sm">
      {/* Video panel: fill screen on mobile; avatar frame 9:16 */}
      <div className={`avatar-video-panel relative flex-1 min-w-0 flex flex-col overflow-hidden max-md:overflow-x-hidden max-md:min-h-0 px-2 md:px-5 max-md:px-1 max-md:pb-0 md:self-stretch md:h-full max-md:flex-1 max-md:min-h-0 ${
        !isMobile && !chatPanelVisible
          ? 'md:min-w-0 md:max-w-[720px] md:flex-[1_1_auto] md:mx-auto'
          : 'md:min-w-[320px] md:max-w-[460px] md:flex-[0_0_38%]'
      } ${
        chatPanelVisible && !mobileChatExpanded ? 'max-md:flex-[0_0_48%]' : ''
      }`}>
        <div className="avatar-stage relative flex-1 flex items-center justify-center bg-transparent min-h-[180px] rounded-lg md:rounded-2xl overflow-hidden min-w-0">
          {/* 9:16 avatar frame – keep min height so avatar/image always visible when embedded or via URL */}
          <div className="relative w-full h-full flex items-center justify-center min-h-0 min-w-0">
            <div
              className="avatar-frame-shell relative aspect-[9/16] h-full w-auto max-w-full min-h-[200px] md:max-h-none bg-transparent rounded-lg md:rounded-xl overflow-hidden"
              onKeyDown={(e) => {
                // no-op: interaction is voice-first ("Hey President Lincoln")
              }}
              role={undefined}
              tabIndex={undefined}
              aria-label={undefined}
            >
              {/* Show static President Lincoln image any time the live stream is not yet active */}
              {!sessionActive && (
                <>
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: 'url(/aftelifebot_liocln_background.jpeg)' }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)'
                    }}
                  />
                </>
              )}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={!sessionActive}
                controls={false}
                className="avatar-video-element absolute inset-0 w-full h-full object-cover"
                style={{ visibility: sessionActive && videoReady ? 'visible' : 'hidden' }}
                preload="auto"
                aria-label="Avatar video"
                data-playsinline
                data-webview-inline
              />
            </div>
          </div>
          {/* Status badges moved to bottom above buttons */}
          {isConnecting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
              <div className="w-10 h-10 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mb-2" />
              <span className="text-sm text-white/80">Connecting...</span>
            </div>
          )}
          {errorType && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 p-5 text-center rounded-lg">
              <div className="text-3xl mb-3">⚠️</div>
              <h3 className="text-base font-medium text-white mb-2">Connection issue</h3>
              <p className="text-sm text-white/80 mb-5 max-w-md leading-relaxed">{errorMessage}</p>
              <div className="flex gap-3">
                <button onClick={handleStaticMode} className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded">Archive</button>
                <button onClick={handleStart} className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded">Retry</button>
              </div>
            </div>
          )}
          {!isConnecting && !errorType && !sessionActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4">
              <button
                onClick={() => {
                  trackButtonClick('start_session_cta', { page: 'avatar' });
                  setShowLegalBeforeStart(true);
                }}
                disabled={isConnecting}
                className="px-6 py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded-lg font-medium disabled:opacity-50"
                aria-label="Start session"
              >
                {isConnecting ? 'Starting...' : 'Start session'}
              </button>
            </div>
          )}

          {/* Voice permission help – fix avatar not speaking or listening (iOS and Android) */}
          {voicePermissionHelpVisible && (sessionActive || !isMobile) && (
            <div className="voice-permission-help absolute inset-x-3 bottom-[max(4rem,calc(env(safe-area-inset-bottom)+4rem))] md:bottom-[7.5rem] z-[40] rounded-xl bg-black/80 backdrop-blur-xl text-white px-3 py-3 shadow-lg border border-white/10">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium mb-1">President Lincoln&apos;s ears are plugged!</p>
                <button
                  type="button"
                  onClick={() => setVoicePermissionHelpVisible(false)}
                  className="w-6 h-6 -mt-0.5 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
                  aria-label="Close voice permission help"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {!isMobile ? (
                <p className="text-xs text-white/95 leading-relaxed mt-1">
                  Microphone not enabled, so President Lincoln cannot hear you right now. You can still continue the conversation by typing your questions in the chat panel.
                </p>
              ) : isAndroid ? (
                <ul className="text-xs text-white/95 leading-relaxed mt-1 list-disc list-inside space-y-0.5">
                  <li>End session first.</li>
                  <li>Go to Chrome → Settings → Site settings → find this site.</li>
                  <li>Allow or reset Microphone for the matching URL (delete permission if needed, then set to Allow).</li>
                  <li>Close the tab and open the site again.</li>
                  <li>Start a new session and allow microphone when prompted.</li>
                </ul>
              ) : (
                <ul className="text-xs text-white/95 leading-relaxed mt-1 list-disc list-inside space-y-0.5">
                  <li>End session first.</li>
                  <li>Go to Settings → Privacy &amp; Security.</li>
                  <li>Enable Speech Recognition and Microphone for your browser.</li>
                  <li>Start a new session and allow microphone when prompted.</li>
                  <li>If both are already enabled: End session, start a new session, and allow microphone when prompted.</li>
                </ul>
              )}
            </div>
          )}

          {/* Bottom: status bar + mic mute (left) + End session (right) */}
          {sessionActive && (
            <>
              <div className="session-controls-wrap absolute bottom-[max(0.25rem,env(safe-area-inset-bottom))] left-2 right-2 md:left-4 md:right-4 md:bottom-6 z-30 flex flex-col items-stretch gap-3">
              {showStarterQuestions && (
                <div className="starter-questions flex flex-wrap justify-center gap-2" role="group" aria-label="Suggested questions">
                  {STARTER_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => handleStarterQuestion(q)}
                      disabled={isTalking || isThinking}
                      className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/30 text-white text-xs md:text-sm font-medium hover:bg-black/75 disabled:opacity-50 transition-opacity"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {/* Listening / Thinking / Speaking status above buttons */}
              <div className="session-status-row flex justify-center items-center text-center w-full">
                {isTalking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Speaking…
                  </span>
                ) : isThinking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Thinking…
                  </span>
                ) : isUserSpeaking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Listening…
                  </span>
                ) : sessionActive ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Waiting for your question…
                  </span>
                ) : null}
              </div>
              {/* Mute (left) + End session (right) – compact pill button that never clips */}
              <div className="session-button-row flex flex-row items-center justify-between gap-3 w-full min-w-0">
                <button
                  type="button"
                  onClick={handleToggleMicMute}
                  className={`session-mute-btn min-w-[46px] min-h-[46px] md:min-w-[52px] md:min-h-[52px] px-3 w-auto h-12 md:h-14 rounded-full flex items-center justify-center ${micButtonLabel ? 'gap-2' : 'gap-0'} border-2 text-sm font-medium flex-shrink-0 order-first ${
                    isMicMuted
                      ? 'bg-slate-800 text-white border-slate-700'
                      : 'bg-white text-slate-800 border-slate-300'
                  }`}
                  aria-pressed={isMicMuted}
                  aria-label={!hasMicDevice || isMicPermissionDenied ? 'Microphone not enabled' : (isMicMuted ? 'Unmute microphone' : 'Mute microphone')}
                >
                  {isMicMuted ? (
                    <MicOff className="w-5 h-5 md:w-6 md:h-6" />
                  ) : (
                    <Mic className="w-5 h-5 md:w-6 md:h-6" />
                  )}
                  {micButtonLabel && <span className="text-xs font-semibold tracking-wide">{micButtonLabel}</span>}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackButtonClick('end_session', { page: 'avatar' });
                    handleClose({ reason: 'manual' });
                  }}
                  disabled={isEndingSession}
                  className="session-end-btn flex-shrink-0 max-w-[70%] min-w-0 min-h-[40px] md:min-h-[44px] px-4 sm:px-5 py-2 md:py-2.5 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-600 text-white text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden order-last"
                  aria-label="End session"
                >
                  <span className="truncate">End session</span>
                </button>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Chat panel: desktop only */}
      {!isMobile && chatPanelVisible && (
        <div
          className={`chat-panel chat-panel-desktop flex flex-col min-h-0 min-w-0 bg-white max-md:overflow-x-hidden
            md:relative md:z-auto md:flex-[1_1_62%] md:min-w-[540px] md:max-w-none md:flex-shrink md:flex-grow-0 md:self-stretch md:rounded-xl md:border md:border-slate-300 md:border-r md:border-r-slate-300 md:overflow-hidden
            max-md:w-full max-md:max-w-[100vw] max-md:overflow-hidden max-md:rounded-t-2xl max-md:shadow-lg max-md:flex-shrink-0
            ${mobileChatExpanded ? 'max-md:fixed max-md:inset-0 max-md:z-[60] max-md:pt-[4.5rem]' : 'max-md:flex-1 max-md:mt-0'}
          `}
        >
          <div className={`chat-header flex-shrink-0 flex items-center justify-between px-3 py-2.5 sm:px-4 md:px-5 md:py-4 border-b border-slate-200 bg-white md:bg-slate-50 min-w-0 ${mobileChatExpanded ? 'max-md:sticky max-md:top-0 relative z-[70]' : ''}`}>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm sm:text-base md:text-lg font-medium md:font-semibold text-slate-900 truncate">President Lincoln Live Chat</h2>
              <p className="hidden md:block text-xs text-slate-500 mt-0.5 truncate">
                Speak or type your questions. Responses focus on President Lincoln and U.S. history.
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  trackButtonClick('toggle_mobile_chat_expand', { page: 'avatar' });
                  setMobileChatExpanded((e) => !e);
                }}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600"
                aria-label={mobileChatExpanded ? 'Show split view' : 'Expand chat'}
              >
                {mobileChatExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  trackButtonClick('close_chat', { page: 'avatar' });
                  setChatPanelVisible(false);
                  setMobileChatExpanded(false);
                }}
                className="md:hidden w-8 h-8 rounded flex items-center justify-center bg-sky-100 hover:bg-sky-200 text-sky-700"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div ref={chatMessagesContainerRef} className="chat-messages-area flex-1 min-h-0 md:min-h-[180px] overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-4 md:mx-4 md:mt-4 md:mb-4 bg-slate-50 md:bg-white overscroll-contain border border-slate-200 md:border-slate-300 md:border-r-2 md:border-r-slate-400 rounded-lg md:rounded-xl box-border w-full min-w-0">
            {messages.length === 0 && !isTalking ? (
              <div className="flex flex-col items-center justify-center gap-3 min-h-[200px] text-slate-500 text-sm text-center">
                <span>{sessionActive ? 'Send a message below to start the conversation.' : 'Start a session to chat.'}</span>
              </div>
            ) : (
              <div className="space-y-4 w-full min-w-0" style={{ width: '100%' }}>
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex gap-2 w-full ${msg.role === 'user' ? 'justify-end chat-row-user' : 'justify-start'}`} style={msg.role === 'user' ? { width: '100%', minWidth: '100%' } : { width: '100%', minWidth: 0 }}>
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                        A
                      </div>
                    )}
                    {msg.role === 'user' ? (
                      <>
                        <div className="flex-1 min-w-0" aria-hidden="true" />
                        <div
                          className="chat-bubble-user max-w-[85%] min-w-[3rem] px-3 py-2 rounded-lg flex-shrink-0 bg-white border border-slate-200 text-slate-800 shadow-sm"
                          data-role="user"
                        >
                          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content || ''}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-sky-400 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold" aria-hidden="true">U</div>
                      </>
                    ) : (
                      <div
                        className="max-w-[85%] min-w-0 px-3 py-2 rounded-lg flex-shrink-0 bg-white border border-slate-200 text-slate-800"
                        data-role="assistant"
                      >
                        <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content || ''}</p>
                      </div>
                    )}
                  </div>
                ))}
                {isTalking && (
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                      A
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-white border border-slate-200 flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                {isTalking && (
                  <p className="text-xs text-slate-500 italic mt-1">{AVATAR_DISPLAY_NAME} is typing…</p>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>
          {/* Input bar: rounded input + Send, no mic controls */}
          <div className="chat-input-bar flex-shrink-0 p-3 md:px-4 md:pt-3 md:pb-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-slate-200 bg-white md:bg-slate-50 box-border">
            <div className="chat-input-row">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask anything about President Lincoln or American history..."
                disabled={!sessionActive || isTalking}
                className="flex-1 min-w-0 px-3 py-3 rounded-full border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-400 disabled:opacity-50 bg-white"
                aria-label="Message input"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!chatInput.trim() || !sessionActive || isTalking}
                className="chat-send-btn rounded-full bg-red-500 hover:bg-red-600 disabled:bg-slate-300 flex items-center justify-center text-white shadow-md md:bg-sky-500 md:hover:bg-sky-600"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Footer disclaimer – hidden on mobile so app fits screen without scrolling */}
      <footer className="flex-shrink-0 min-w-0 py-2 px-3 md:px-4 bg-slate-50 border-t border-slate-200 max-md:hidden">
        <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto break-words overflow-hidden">
          {FOOTER_TEXT}
        </p>
      </footer>
    </div>
  );
}

export default AvatarComponent;
